import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDownUp, ArrowRight, ChevronDown, ChevronLeft, ChevronRight, Circle, ClipboardPaste, Copy, CopyPlus, Download, Eraser, Eye, FolderOpen, Hand, Highlighter, Layers, LineChart, Minus, MousePointer2, PenTool, Plus, Redo2, RotateCcw, Save, Scissors, Trash2, Type, Undo2, X } from 'lucide-react';
import { convertLatexToMarkup } from 'mathlive';
import { InlineMathModal } from '../math/InlineMathField';
import MathRenderer from '../math/MathRenderer';
import { FirestoreService } from '../../services/firestore';
import { useAuthStore } from '../../stores/authStore';
import { canSaveToolWork, getToolSaveMessage } from '../../utils/subscription';
import {
  GRAPH_SKETCH_SCHEMA_EXAMPLE,
  parseGraphSketchDocumentSchema,
} from './graphSketchSchema';

const DEFAULT_VIEWPORT = { xMin: -10, xMax: 10, yMin: -10, yMax: 10 };
const TRIG_VIEWPORT = { ...DEFAULT_VIEWPORT, xMin: -180, xMax: 180 };
const GRAPH_COLOR_OPTIONS = [
  { id: 'blue', value: '#2563EB', label: 'Blue' },
  { id: 'red', value: '#DC2626', label: 'Red' },
  { id: 'green', value: '#059669', label: 'Green' },
  { id: 'purple', value: '#7C3AED', label: 'Purple' },
  { id: 'orange', value: '#EA580C', label: 'Orange' },
  { id: 'teal', value: '#0891B2', label: 'Teal' },
  { id: 'slate', value: '#1E293B', label: 'Slate' },
];
const PAD = 24;
const SAMPLES = 720;
const TABLE_ROWS = [
  { x: '-2', y: '4' },
  { x: '-1', y: '1' },
  { x: '0', y: '0' },
  { x: '1', y: '1' },
  { x: '2', y: '4' },
];

const EQUATIONS = {
  linear: { label: 'y = mx + b', latex: 'y=mx+b', params: ['m', 'b'], defaults: { m: '1', b: '0' } },
  verticalLine: {
    label: 'x = c',
    latex: 'x=c',
    params: ['c'],
    defaults: { c: '0' },
  },
  cubicStandard: {
    label: 'y = ax^3 + bx^2 + cx + d',
    latex: 'y=ax^3+bx^2+cx+d',
    params: ['a', 'b', 'c', 'd'],
    defaults: { a: '1', b: '0', c: '0', d: '0' },
  },
  quadraticStandard: {
    label: 'y = ax^2 + bx + c',
    latex: 'y=ax^2+bx+c',
    params: ['a', 'b', 'c'],
    defaults: { a: '1', b: '0', c: '0' },
  },
  quadraticVertex: {
    label: 'y = a(x - p)^2 + q',
    latex: 'y=a(x-p)^2+q',
    params: ['a', 'p', 'q'],
    defaults: { a: '1', p: '0', q: '0' },
  },
  hyperbola: {
    label: 'y = k / (x - p) + q',
    latex: 'y=\\frac{k}{x-p}+q',
    params: ['k', 'p', 'q'],
    defaults: { k: '1', p: '0', q: '0' },
  },
  exponential: {
    label: 'y = a.b^(x - p) + q',
    latex: 'y=a\\cdot b^{(x-p)}+q',
    params: ['a', 'b', 'p', 'q'],
    defaults: { a: '1', b: '2', p: '0', q: '0' },
  },
  sine: {
    label: 'y = A sin(k(x + p)) + q',
    latex: 'y=A\\sin(k(x+p))+q',
    params: ['A', 'k', 'p', 'q'],
    defaults: { A: '1', k: '1', p: '0', q: '0' },
  },
  cosine: {
    label: 'y = A cos(k(x + p)) + q',
    latex: 'y=A\\cos(k(x+p))+q',
    params: ['A', 'k', 'p', 'q'],
    defaults: { A: '1', k: '1', p: '0', q: '0' },
  },
  tangent: {
    label: 'y = A tan(k(x + p)) + q',
    latex: 'y=A\\tan(k(x+p))+q',
    params: ['A', 'k', 'p', 'q'],
    defaults: { A: '1', k: '1', p: '0', q: '0' },
  },
};
const TRIG_EQUATION_TYPES = ['sine', 'cosine', 'tangent'];
const EQUATION_GROUP_OPTIONS = [
  { id: 'normal', label: 'Normal' },
  { id: 'trig', label: 'Trig' },
];
const isTrigEquation = (type) => TRIG_EQUATION_TYPES.includes(type);
const equationGroupForType = (type) => (isTrigEquation(type) ? 'trig' : 'normal');
const equationTypesForGroup = (group) =>
  Object.keys(EQUATIONS).filter((type) => (group === 'trig' ? isTrigEquation(type) : !isTrigEquation(type)));
const normalizeEquationType = (type) => (EQUATIONS[type] ? type : 'linear');
const normalizeEquationGroup = (group) => (EQUATION_GROUP_OPTIONS.some((option) => option.id === group) ? group : 'normal');
const equationTypeForGroup = (group, type) => {
  const normalizedGroup = normalizeEquationGroup(group);
  const types = equationTypesForGroup(normalizedGroup);
  return types.includes(type) ? type : types[0];
};
const viewportForEquationGroup = (group, current = DEFAULT_VIEWPORT) => ({
  ...current,
  xMin: group === 'trig' ? TRIG_VIEWPORT.xMin : DEFAULT_VIEWPORT.xMin,
  xMax: group === 'trig' ? TRIG_VIEWPORT.xMax : DEFAULT_VIEWPORT.xMax,
});

const ANALYSIS = [
  ['positive', 'f(x) > 0', 'f(x)>0'],
  ['negative', 'f(x) < 0', 'f(x)<0'],
  ['compare', 'f(x) > g(x)', 'f(x)>g(x)'],
  ['increasing', "f'(x) > 0", "f'(x)>0"],
  ['decreasing', "f'(x) < 0", "f'(x)<0"],
  ['productPositive', 'f(x).g(x) > 0', 'f(x)\\cdot g(x)>0'],
  ['quotientPositive', 'f(x)/g(x) > 0', '\\frac{f(x)}{g(x)}>0'],
  ['inverse', 'Inverse reflection', 'f^{-1}(x)\\text{ reflection about }y=x'],
];

const num = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};
const degToRad = (degrees) => (degrees * Math.PI) / 180;

const fmt = (value) => {
  if (!Number.isFinite(value)) return 'undefined';
  if (Math.abs(value) < 1e-9) return '0';
  const fixed = Math.abs(value) >= 1000 || Math.abs(value) < 0.01 ? value.toExponential(2) : value.toFixed(2);
  return fixed.replace(/\.00$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
};

const makeParams = (type) => ({ ...EQUATIONS[type].defaults });
const graphId = () => `graph-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
const nextColor = (graphs) => GRAPH_COLOR_OPTIONS[graphs.length % GRAPH_COLOR_OPTIONS.length].value;

const evalEquation = (type, params, x) => {
  switch (type) {
    case 'linear':
      return params.m * x + params.b;
    case 'verticalLine':
      return null;
    case 'cubicStandard':
      return params.a * x * x * x + params.b * x * x + params.c * x + params.d;
    case 'quadraticStandard':
      return params.a * x * x + params.b * x + params.c;
    case 'quadraticVertex':
      return params.a * (x - params.p) * (x - params.p) + params.q;
    case 'hyperbola':
      return Math.abs(x - params.p) < 1e-9 ? null : params.k / (x - params.p) + params.q;
    case 'exponential':
      return params.b <= 0 || Math.abs(params.b - 1) < 1e-9 ? null : params.a * params.b ** (x - params.p) + params.q;
    case 'sine':
      return params.A * Math.sin(degToRad(params.k * (x + params.p))) + params.q;
    case 'cosine':
      return params.A * Math.cos(degToRad(params.k * (x + params.p))) + params.q;
    case 'tangent':
      return params.A * Math.tan(degToRad(params.k * (x + params.p))) + params.q;
    default:
      return null;
  }
};

const resolveStraightLineGraph = (graph, graphMap, depth = 0) => {
  if (!graph || depth > 5) return null;
  if (graph.kind === 'function' && graph.equationType === 'verticalLine') {
    return { orientation: 'vertical', value: graph.params.c };
  }
  if (graph.kind === 'inverse') {
    const source = resolveStraightLineGraph(graphMap.get(graph.sourceGraphId), graphMap, depth + 1);
    if (!source) return null;
    return {
      orientation: source.orientation === 'vertical' ? 'horizontal' : 'vertical',
      value: source.value,
    };
  }
  if (graph.kind === 'transform') {
    const source = resolveStraightLineGraph(graphMap.get(graph.sourceGraphId), graphMap, depth + 1);
    if (!source) return null;
    if (source.orientation === 'vertical') {
      let value = source.value - graph.horizontalShift;
      if (graph.reflectY) value *= -1;
      return { orientation: 'vertical', value };
    }
    let value = source.value + graph.verticalShift;
    if (graph.reflectX) value *= -1;
    return { orientation: 'horizontal', value };
  }
  return null;
};

const derivativeStep = (x) => Math.max(1e-4, Math.abs(x) * 1e-4, 1e-3);

const derivativeAt = (graph, x, graphMap, depth = 0) => {
  if (!graph || depth > 5) return null;
  const h = derivativeStep(x);
  const left = evalGraph(graph, x - h, graphMap, depth + 1);
  const right = evalGraph(graph, x + h, graphMap, depth + 1);
  if (!Number.isFinite(left) || !Number.isFinite(right)) return null;
  return (right - left) / (2 * h);
};

const polynomialLatex = (terms) => {
  const parts = [];
  terms.forEach(({ coefficient, body }) => {
    if (!Number.isFinite(coefficient) || Math.abs(coefficient) < 1e-9) return;
    const magnitude = Math.abs(coefficient);
    const formatted = fmt(magnitude);
    const signedBody = body
      ? `${Math.abs(magnitude - 1) < 1e-9 ? '' : formatted}${body}`
      : formatted;
    if (!parts.length) {
      parts.push(`${coefficient < 0 ? '-' : ''}${signedBody}`);
      return;
    }
    parts.push(`${coefficient < 0 ? '-' : '+'}${signedBody}`);
  });
  return parts.length ? parts.join('') : '0';
};

const appendSignedTerm = (value) => {
  if (!Number.isFinite(value) || Math.abs(value) < 1e-9) return '';
  return value < 0 ? `${fmt(value)}` : `+${fmt(value)}`;
};

const shiftedVariableLatex = (variable, shift) => {
  if (!Number.isFinite(shift) || Math.abs(shift) < 1e-9) return variable;
  return `${variable}${shift < 0 ? '+' : '-'}${fmt(Math.abs(shift))}`;
};

const addedVariableLatex = (variable, shift) => {
  if (!Number.isFinite(shift) || Math.abs(shift) < 1e-9) return variable;
  return `${variable}${shift < 0 ? '-' : '+'}${fmt(Math.abs(shift))}`;
};

const scaledExpressionLatex = (coefficient, expression) => {
  if (!Number.isFinite(coefficient) || Math.abs(coefficient) < 1e-9) return '0';
  if (Math.abs(coefficient - 1) < 1e-9) return expression;
  if (Math.abs(coefficient + 1) < 1e-9) return `-${expression}`;
  return `${fmt(coefficient)}${expression}`;
};

const exponentialBaseLatex = (base) => {
  const value = num(base);
  return value < 0 ? `(${fmt(value)})` : fmt(value);
};

const multipliedExpressionLatex = (coefficient, expression) => {
  const value = num(coefficient);
  if (!Number.isFinite(value) || Math.abs(value) < 1e-9) return '0';
  if (Math.abs(value - 1) < 1e-9) return expression;
  if (Math.abs(value + 1) < 1e-9) return `-${expression}`;
  return `${fmt(value)}\\cdot${expression}`;
};

const functionGraphLatex = (graph) => {
  const params = graph?.params || {};
  switch (graph?.equationType) {
    case 'linear':
      return `y=${polynomialLatex([
        { coefficient: params.m, body: 'x' },
        { coefficient: params.b, body: '' },
      ])}`;
    case 'verticalLine':
      return `x=${fmt(params.c)}`;
    case 'cubicStandard':
      return `y=${polynomialLatex([
        { coefficient: params.a, body: 'x^3' },
        { coefficient: params.b, body: 'x^2' },
        { coefficient: params.c, body: 'x' },
        { coefficient: params.d, body: '' },
      ])}`;
    case 'quadraticStandard':
      return `y=${polynomialLatex([
        { coefficient: params.a, body: 'x^2' },
        { coefficient: params.b, body: 'x' },
        { coefficient: params.c, body: '' },
      ])}`;
    case 'quadraticVertex':
      return `y=${scaledExpressionLatex(params.a, `(${shiftedVariableLatex('x', params.p)})^2`)}${appendSignedTerm(params.q)}`;
    case 'hyperbola':
      return `y=\\frac{${fmt(params.k)}}{${shiftedVariableLatex('x', params.p)}}${appendSignedTerm(params.q)}`;
    case 'exponential':
      return `y=${multipliedExpressionLatex(params.a, `${exponentialBaseLatex(params.b)}^{(${shiftedVariableLatex('x', params.p)})}`)}${appendSignedTerm(params.q)}`;
    case 'sine':
      return `y=${scaledExpressionLatex(params.A, `\\sin(${fmt(params.k)}(${addedVariableLatex('x', params.p)}))`)}${appendSignedTerm(params.q)}`;
    case 'cosine':
      return `y=${scaledExpressionLatex(params.A, `\\cos(${fmt(params.k)}(${addedVariableLatex('x', params.p)}))`)}${appendSignedTerm(params.q)}`;
    case 'tangent':
      return `y=${scaledExpressionLatex(params.A, `\\tan(${fmt(params.k)}(${addedVariableLatex('x', params.p)}))`)}${appendSignedTerm(params.q)}`;
    default:
      return EQUATIONS[graph?.equationType]?.latex || graph?.title || '\\text{Function}';
  }
};

const graphLatex = (graph, graphMap) => {
  if (!graph) return '\\text{No graph selected}';
  const straightLine = resolveStraightLineGraph(graph, graphMap);
  if (straightLine?.orientation === 'vertical') return `x=${fmt(straightLine.value)}`;
  if (straightLine?.orientation === 'horizontal') return `y=${fmt(straightLine.value)}`;
  if (graph.kind === 'function') return functionGraphLatex(graph);
  if (graph.kind === 'table') return `\\text{${graph.title}}`;
  if (graph.kind === 'transform') {
    const source = graphMap.get(graph.sourceGraphId);
    const baseLatex = graphLatex(source, graphMap);
    const transformedInput = graph.reflectY ? `-${baseLatex}` : baseLatex;
    return `\\text{Transform of }${transformedInput}`;
  }
  if (graph.kind === 'derivative') {
    const source = graphMap.get(graph.sourceGraphId);
    return derivativeLatex(source, graphMap);
  }
  if (graph.kind === 'inverse') return 'f^{-1}(x)';
  return `\\text{${graph.title}}`;
};

function derivativeLatex(graph, graphMap) {
  if (!graph) return "f'(x)";
  if (graph.kind === 'function') {
    const params = graph.params || {};
    switch (graph.equationType) {
      case 'linear':
        return `y=${fmt(params.m)}`;
      case 'verticalLine':
        return "\\text{undefined for }x=c";
      case 'cubicStandard':
        return `y=${polynomialLatex([
          { coefficient: 3 * params.a, body: 'x^2' },
          { coefficient: 2 * params.b, body: 'x' },
          { coefficient: params.c, body: '' },
        ])}`;
      case 'quadraticStandard':
        return `y=${polynomialLatex([
          { coefficient: 2 * params.a, body: 'x' },
          { coefficient: params.b, body: '' },
        ])}`;
      case 'quadraticVertex':
        return `y=${fmt(2 * params.a)}(x-${fmt(params.p)})`;
      case 'hyperbola':
        return `y=\\frac{${fmt(-params.k)}}{(x-${fmt(params.p)})^2}`;
      case 'exponential':
        return `y=${multipliedExpressionLatex(params.a, `${exponentialBaseLatex(params.b)}^{(x-${fmt(params.p)})}\\ln(${exponentialBaseLatex(params.b)})`)}`;
      case 'sine':
        return `y=${fmt(params.A * params.k)}\\cos(${fmt(params.k)}(${addedVariableLatex('x', params.p)}))`;
      case 'cosine':
        return `y=${fmt(-params.A * params.k)}\\sin(${fmt(params.k)}(${addedVariableLatex('x', params.p)}))`;
      case 'tangent':
        return `y=${fmt(params.A * params.k)}\\sec^2(${fmt(params.k)}(${addedVariableLatex('x', params.p)}))`;
      default:
        break;
    }
  }
  if (graph.kind === 'derivative') {
    const source = graphMap.get(graph.sourceGraphId);
    return `\\frac{d}{dx}\\left(${derivativeLatex(source, graphMap)}\\right)`;
  }
  if (graph.kind === 'transform' || graph.kind === 'table' || graph.kind === 'inverse') {
    return `\\frac{d}{dx}\\left(${graphLatex(graph, graphMap)}\\right)`;
  }
  return "f'(x)";
}

const tableValue = (points, x) => {
  if (points.length < 2) return null;
  const sorted = [...points].sort((a, b) => a.x - b.x);
  if (x < sorted[0].x || x > sorted[sorted.length - 1].x) return null;
  for (let i = 0; i < sorted.length - 1; i += 1) {
    const start = sorted[i];
    const end = sorted[i + 1];
    if (x === start.x) return start.y;
    if (x === end.x) return end.y;
    if (x > start.x && x < end.x) {
      const ratio = (x - start.x) / (end.x - start.x);
      return start.y + ratio * (end.y - start.y);
    }
  }
  return null;
};

const evalGraph = (graph, x, graphMap, depth = 0) => {
  if (!graph || depth > 5) return null;
  const straightLine = resolveStraightLineGraph(graph, graphMap, depth + 1);
  if (straightLine?.orientation === 'horizontal') return straightLine.value;
  if (straightLine?.orientation === 'vertical') return null;
  if (graph.kind === 'function') return evalEquation(graph.equationType, graph.params, x);
  if (graph.kind === 'table') return tableValue(graph.points, x);
  if (graph.kind === 'derivative') {
    const source = graphMap.get(graph.sourceGraphId);
    const sourceLine = resolveStraightLineGraph(source, graphMap, depth + 1);
    if (sourceLine?.orientation === 'horizontal') return 0;
    if (sourceLine?.orientation === 'vertical') return null;
    return derivativeAt(source, x, graphMap, depth);
  }
  if (graph.kind === 'transform') {
    const source = graphMap.get(graph.sourceGraphId);
    const input = (graph.reflectY ? -x : x) + graph.horizontalShift;
    const base = evalGraph(source, input, graphMap, depth + 1);
    if (!Number.isFinite(base)) return null;
    return (graph.reflectX ? -base : base) + graph.verticalShift;
  }
  return null;
};

const getRenderMetrics = (viewport, size, zoomLevel = 1, axisStep = GRID_UNIT_STEP, xAxisStep = axisStep) => {
  const left = PAD;
  const right = size.width - PAD;
  const top = PAD;
  const bottom = size.height - PAD;
  const width = Math.max(1, right - left);
  const height = Math.max(1, bottom - top);
  const yUnitStep = Math.max(1e-6, Number(axisStep) || GRID_UNIT_STEP);
  const xUnitStep = Math.max(1e-6, Number(xAxisStep) || yUnitStep);
  const majorGridPx = BASE_MAJOR_GRID_PX * GRID_SIZE_MULTIPLIER * zoomLevel;
  const xScale = majorGridPx / xUnitStep;
  const yScale = majorGridPx / yUnitStep;
  const yCenter = (viewport.yMin + viewport.yMax) / 2;
  const visibleYSpan = height / yScale;
  const visibleYMin = yCenter - visibleYSpan / 2;
  const visibleYMax = yCenter + visibleYSpan / 2;
  const xCenter = (viewport.xMin + viewport.xMax) / 2;
  const xSpan = width / xScale;
  const xMin = xCenter - xSpan / 2;
  const xMax = xCenter + xSpan / 2;
  return {
    left,
    top,
    width,
    height,
    right: left + width,
    bottom: top + height,
    scale: yScale,
    xScale,
    yScale,
    viewport: {
      xMin,
      xMax,
      yMin: visibleYMin,
      yMax: visibleYMax,
    },
  };
};

const toCanvas = (point, viewport, size, zoomLevel = 1, axisStep = GRID_UNIT_STEP, xAxisStep = axisStep) => {
  const plot = getRenderMetrics(viewport, size, zoomLevel, axisStep, xAxisStep);
  return {
    x: plot.left + ((point.x - plot.viewport.xMin) / (plot.viewport.xMax - plot.viewport.xMin)) * plot.width,
    y: plot.bottom - ((point.y - plot.viewport.yMin) / (plot.viewport.yMax - plot.viewport.yMin)) * plot.height,
  };
};

const normalizeNiceIncrement = (rough) => {
  const safeRough = Math.abs(rough) || 1;
  const mag = 10 ** Math.floor(Math.log10(safeRough));
  const norm = safeRough / mag;
  if (norm < 1.5) return mag;
  if (norm < 3) return mag * 2;
  if (norm < 7) return mag * 5;
  return mag * 10;
};

const niceStep = (min, max) => {
  const span = Math.abs(max - min) || 1;
  const rough = span / 10;
  return normalizeNiceIncrement(rough);
};

const stepForPixelSpacing = (min, max, pixels, pixelSpacing) => {
  const span = Math.abs(max - min) || 1;
  const safePixels = Math.max(1, pixels);
  const unitsForTarget = (span / safePixels) * pixelSpacing;
  return normalizeNiceIncrement(unitsForTarget);
};

const intervalsFor = (predicate, viewport) => {
  const intervals = [];
  const span = viewport.xMax - viewport.xMin;
  let start = null;
  for (let i = 0; i <= 520; i += 1) {
    const x = viewport.xMin + (i / 520) * span;
    const value = predicate(x);
    if (value === true && start === null) start = x;
    if (value !== true && start !== null) {
      intervals.push([start, x]);
      start = null;
    }
  }
  if (start !== null) intervals.push([start, viewport.xMax]);
  return intervals.filter(([startValue, endValue]) => endValue - startValue > 0.02);
};

const formatIntervals = (intervals) =>
  intervals.length ? intervals.map(([start, end]) => `${fmt(start)} to ${fmt(end)}`).join(', ') : 'No intervals visible in the current window.';

const renderLatexMarkup = (latex) => {
  try {
    return convertLatexToMarkup(latex, { defaultMode: 'inline-math' });
  } catch {
    return latex;
  }
};

const buildCursorUrl = (svg) => `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;

const TOOL_CURSORS = {
  pointer: 'default',
  pan: 'grab',
  pen: `${buildCursorUrl(
    `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
      <path d="M7 21l2.5-6.5L18.5 5.5a2 2 0 0 1 2.8 0l1.2 1.2a2 2 0 0 1 0 2.8l-9 9L7 21z" fill="#0F172A" stroke="white" stroke-width="1.5"/>
      <path d="M15.5 8.5l4 4" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
    </svg>`
  )} 9 19, auto`,
  highlighter: `${buildCursorUrl(
    `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30">
      <path d="M8 20l7-11 7 4-7 11H8z" fill="#FACC15" stroke="#854D0E" stroke-width="1.5"/>
      <path d="M6 22h12" stroke="#F59E0B" stroke-width="3" stroke-linecap="round" opacity="0.8"/>
    </svg>`
  )} 10 18, auto`,
  eraser: `${buildCursorUrl(
    `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
      <path d="M10 7l9 9-5 5H8l-4-4 6-10z" fill="#F8FAFC" stroke="#475569" stroke-width="1.5"/>
      <path d="M16 10l4 4" stroke="#CBD5E1" stroke-width="1.5"/>
    </svg>`
  )} 11 18, auto`,
};

const MIN_SELECTION_SIZE = 8;
const SELECTION_RESIZE_HANDLE_SIZE = 10;
const MIN_IMAGE_SIZE = 32;
const IMAGE_RESIZE_HANDLE_SIZE = 14;
const MARKER_HANDLE_RADIUS = 7;
const MIN_ARROW_LENGTH = 0;
const MAX_ARROW_LENGTH = 160;
const MIN_DOT_RADIUS = 3;
const MAX_DOT_RADIUS = 36;
const TEXT_SIZE_OPTIONS = [
  { id: 'S', size: 16 },
  { id: 'M', size: 24 },
  { id: 'L', size: 32 },
];
const TEXT_MODE_OPTIONS = [
  { id: 'plain', label: 'Text' },
  { id: 'math', label: 'Math' },
];
const GRID_MODE_OPTIONS = [
  { id: 'cartesian', label: 'Cartesian grid', description: 'Coordinate grid with scaled graph lines.' },
  { id: 'graphPaper', label: 'Graph paper', description: 'Square paper grid for sketching and annotations.' },
  { id: 'plain', label: 'Plain white', description: 'Clear background with no grid.' },
];
const GRAPH_LINE_STYLE_OPTIONS = [
  { id: 'solid', label: 'Solid' },
  { id: 'dashed', label: 'Dashed' },
  { id: 'dotted', label: 'Dotted' },
];
const AXIS_STEP_OPTIONS = [
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 5, label: '5' },
];
const X_AXIS_MARKER_OPTIONS = [
  { id: 'number', label: 'Numbers', degreeStep: null },
  { id: 'degree30', label: '30°', degreeStep: 30 },
  { id: 'degree45', label: '45°', degreeStep: 45 },
  { id: 'degree90', label: '90°', degreeStep: 90 },
];
const GRID_SIZE_MULTIPLIER = 1.5;
const BASE_MAJOR_GRID_PX = 48;
const GRID_UNIT_STEP = 1;
const HANDWRITING_LINE_SPACING = 32;
const HANDWRITING_MARGIN_X = 52;
const LOCAL_GRAPH_SKETCHES_KEY = 'graph-sketch-tool-saves';
const DEFAULT_SKETCH_TITLE = 'Untitled graph sketch';
const DEFAULT_SLIDE_TITLE_PREFIX = 'Canvas';
const DEFAULT_CANVAS_BACKGROUND_COLOR = '#FFFFFF';
const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;
const RGB_COLOR_PATTERN = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i;

const getGraphLineDash = (lineStyle) => {
  if (lineStyle === 'dashed') return [10, 6];
  if (lineStyle === 'dotted') return [2, 6];
  return [];
};

const normalizeSelectionRect = (rect) => ({
  x: Math.min(rect.x, rect.x + rect.width),
  y: Math.min(rect.y, rect.y + rect.height),
  width: Math.abs(rect.width),
  height: Math.abs(rect.height),
});

const getSelectionResizeHandles = (rect) => {
  const normalized = normalizeSelectionRect(rect);
  const left = normalized.x;
  const centerX = normalized.x + normalized.width / 2;
  const right = normalized.x + normalized.width;
  const top = normalized.y;
  const centerY = normalized.y + normalized.height / 2;
  const bottom = normalized.y + normalized.height;

  return [
    { id: 'nw', x: left, y: top, cursor: 'nwse-resize' },
    { id: 'n', x: centerX, y: top, cursor: 'ns-resize' },
    { id: 'ne', x: right, y: top, cursor: 'nesw-resize' },
    { id: 'e', x: right, y: centerY, cursor: 'ew-resize' },
    { id: 'se', x: right, y: bottom, cursor: 'nwse-resize' },
    { id: 's', x: centerX, y: bottom, cursor: 'ns-resize' },
    { id: 'sw', x: left, y: bottom, cursor: 'nesw-resize' },
    { id: 'w', x: left, y: centerY, cursor: 'ew-resize' },
  ];
};

const getSelectionResizeHandleBounds = (handle) => ({
  x: handle.x - SELECTION_RESIZE_HANDLE_SIZE / 2,
  y: handle.y - SELECTION_RESIZE_HANDLE_SIZE / 2,
  width: SELECTION_RESIZE_HANDLE_SIZE,
  height: SELECTION_RESIZE_HANDLE_SIZE,
});

const getSelectionResizeAnchor = (rect, handleId) => {
  const normalized = normalizeSelectionRect(rect);
  const left = normalized.x;
  const centerX = normalized.x + normalized.width / 2;
  const right = normalized.x + normalized.width;
  const top = normalized.y;
  const centerY = normalized.y + normalized.height / 2;
  const bottom = normalized.y + normalized.height;
  const anchors = {
    nw: { x: right, y: bottom },
    n: { x: centerX, y: bottom },
    ne: { x: left, y: bottom },
    e: { x: left, y: centerY },
    se: { x: left, y: top },
    s: { x: centerX, y: top },
    sw: { x: right, y: top },
    w: { x: right, y: centerY },
  };
  return anchors[handleId] || { x: left, y: top };
};

const resizeSelectionRect = (startRect, handleId, anchor, point) => {
  const normalized = normalizeSelectionRect(startRect);
  const left = handleId.includes('w') ? point.x : handleId.includes('e') ? anchor.x : normalized.x;
  const right = handleId.includes('e') ? point.x : handleId.includes('w') ? anchor.x : normalized.x + normalized.width;
  const top = handleId.includes('n') ? point.y : handleId.includes('s') ? anchor.y : normalized.y;
  const bottom = handleId.includes('s') ? point.y : handleId.includes('n') ? anchor.y : normalized.y + normalized.height;

  return normalizeSelectionRect({
    x: left,
    y: top,
    width: right - left,
    height: bottom - top,
  });
};

const getTextBounds = (item) => {
  const width = Math.max(24, item.text.length * item.size * 0.62);
  return {
    x: item.x,
    y: item.y,
    width,
    height: item.size * 1.2,
  };
};

const getMarkerBounds = (item) => {
  if (item.kind === 'dot') {
    const radius = item.radius || 7;
    return { x: item.x - radius - 6, y: item.y - radius - 6, width: radius * 2 + 12, height: radius * 2 + 12 };
  }
  const length = item.length || 34;
  const angle = ((item.rotation || 0) * Math.PI) / 180;
  const half = length / 2;
  const extentX = Math.abs(Math.cos(angle)) * half + Math.abs(Math.sin(angle)) * 11 + 6;
  const extentY = Math.abs(Math.sin(angle)) * half + Math.abs(Math.cos(angle)) * 11 + 6;
  return { x: item.x - extentX, y: item.y - extentY, width: extentX * 2, height: extentY * 2 };
};

const rotateMarkerPoint = (item, localX, localY) => {
  const angle = ((item.rotation || 0) * Math.PI) / 180;
  return {
    x: item.x + localX * Math.cos(angle) - localY * Math.sin(angle),
    y: item.y + localX * Math.sin(angle) + localY * Math.cos(angle),
  };
};

const getMarkerHandles = (item) => {
  if (item.kind === 'dot') {
    const radius = item.radius || 7;
    return [{ id: 'resize', ...rotateMarkerPoint(item, radius, radius) }];
  }
  const length = item.length || 34;
  return [
    { id: 'resize', ...rotateMarkerPoint(item, -length / 2, 0) },
    { id: 'rotate', ...rotateMarkerPoint(item, 0, -30) },
  ];
};

const drawMarker = (ctx, item) => {
  ctx.save();
  ctx.translate(item.x, item.y);
  ctx.rotate(((item.rotation || 0) * Math.PI) / 180);
  ctx.fillStyle = item.color || '#1E293B';
  ctx.strokeStyle = item.color || '#1E293B';
  if (item.kind === 'dot') {
    ctx.beginPath();
    ctx.arc(0, 0, item.radius || 7, 0, Math.PI * 2);
    ctx.fill();
  } else {
    const length = item.length || 34;
    const half = length / 2;
    ctx.lineWidth = item.lineWidth || 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.setLineDash(getGraphLineDash(item.lineStyle || 'solid'));
    ctx.beginPath();
    ctx.moveTo(-half, 0);
    ctx.lineTo(half, 0);
    ctx.lineTo(half - 10, -7);
    ctx.moveTo(half, 0);
    ctx.lineTo(half - 10, 7);
    ctx.stroke();
  }
  ctx.restore();
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const fitImageToCanvas = (width, height, maxWidth, maxHeight) => {
  const safeWidth = Math.max(1, width || 1);
  const safeHeight = Math.max(1, height || 1);
  const scale = Math.min(1, maxWidth / safeWidth, maxHeight / safeHeight);
  return {
    width: Math.max(MIN_IMAGE_SIZE, Math.round(safeWidth * scale)),
    height: Math.max(MIN_IMAGE_SIZE, Math.round(safeHeight * scale)),
  };
};

const getImageBounds = (item) => ({
  x: item.x,
  y: item.y,
  width: item.width,
  height: item.height,
});

const getImageResizeHandleBounds = (bounds) => ({
  x: bounds.x + bounds.width - IMAGE_RESIZE_HANDLE_SIZE,
  y: bounds.y + bounds.height - IMAGE_RESIZE_HANDLE_SIZE,
  width: IMAGE_RESIZE_HANDLE_SIZE,
  height: IMAGE_RESIZE_HANDLE_SIZE,
});

const getImageCanvasPlacement = (item) => (item?.canvasPlacement === 'behind' ? 'behind' : 'front');
const isBehindCanvasImage = (item) => item?.kind === 'image' && getImageCanvasPlacement(item) === 'behind';
const normalizeAnnotationItem = (item) => {
  if (!item || typeof item !== 'object') return item;
  if (item.kind !== 'image') return item;
  return {
    ...item,
    canvasPlacement: getImageCanvasPlacement(item),
  };
};
const normalizeGraph = (graph) => ({
  ...graph,
  visible: graph?.visible ?? true,
});

const pointInBounds = (point, bounds) =>
  point.x >= bounds.x &&
  point.x <= bounds.x + bounds.width &&
  point.y >= bounds.y &&
  point.y <= bounds.y + bounds.height;

const encodeSvg = (svg) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
const CSS_URL_PATTERN = /url\(\s*(['"]?)([^'")]+)\1\s*\)/g;
const readBlobAsDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});
const cloneValue = (value) => JSON.parse(JSON.stringify(value));
const translateAnnotationItem = (item, deltaX, deltaY) => {
  if (!item || typeof item !== 'object') return item;
  if (Array.isArray(item.points)) {
    return {
      ...item,
      points: item.points.map((point) => ({
        ...point,
        x: point.x + deltaX,
        y: point.y + deltaY,
      })),
    };
  }
  if (Number.isFinite(item.x) && Number.isFinite(item.y)) {
    return {
      ...item,
      x: item.x + deltaX,
      y: item.y + deltaY,
    };
  }
  return item;
};
const createLocalSketchId = () => `local-graph-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const createSlideId = () => `graph-slide-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const normalizeAxisStep = (value) => {
  const parsed = Number(value);
  return AXIS_STEP_OPTIONS.some((option) => option.value === parsed) ? parsed : 1;
};
const normalizeXAxisMarker = (value) => (X_AXIS_MARKER_OPTIONS.some((option) => option.id === value) ? value : 'number');
const getXAxisMarkerOption = (value) =>
  X_AXIS_MARKER_OPTIONS.find((option) => option.id === normalizeXAxisMarker(value)) || X_AXIS_MARKER_OPTIONS[0];
const formatXAxisLabel = (value, markerMode) =>
  getXAxisMarkerOption(markerMode).degreeStep ? `${fmt(value)}°` : fmt(value);

const normalizeHexColor = (value) => {
  const trimmed = String(value || '').trim();
  if (!HEX_COLOR_PATTERN.test(trimmed)) return null;
  if (trimmed.length === 4) {
    return `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`.toUpperCase();
  }
  return trimmed.toUpperCase();
};
const normalizeRgbColor = (value) => {
  const match = String(value || '').trim().match(RGB_COLOR_PATTERN);
  if (!match) return null;
  const [red, green, blue] = match.slice(1, 4).map(Number);
  if ([red, green, blue].some((channel) => !Number.isInteger(channel) || channel < 0 || channel > 255)) return null;
  const alpha = match[4];
  if (alpha !== undefined) {
    const parsedAlpha = Number(alpha);
    if (!Number.isFinite(parsedAlpha) || parsedAlpha < 0 || parsedAlpha > 1) return null;
    return `rgba(${red}, ${green}, ${blue}, ${parsedAlpha})`;
  }
  return `rgb(${red}, ${green}, ${blue})`;
};
const normalizeCanvasBackgroundColor = (value) => {
  const trimmed = String(value || '').trim();
  return normalizeHexColor(trimmed) || normalizeRgbColor(trimmed) || DEFAULT_CANVAS_BACKGROUND_COLOR;
};
const isValidCanvasBackgroundColor = (value) =>
  Boolean(normalizeHexColor(value) || normalizeRgbColor(value));
const getColorInputValue = (value) =>
  normalizeHexColor(value) || DEFAULT_CANVAS_BACKGROUND_COLOR;

const defaultTransformState = () => ({
  horizontalShift: '0',
  verticalShift: '0',
  reflectX: false,
  reflectY: false,
  mode: 'copy',
});

const normalizeCanvasState = (state = {}) => ({
  graphs: Array.isArray(state?.graphs) ? cloneValue(state.graphs).map(normalizeGraph) : [],
  viewport: state?.viewport ? { ...DEFAULT_VIEWPORT, ...state.viewport } : DEFAULT_VIEWPORT,
  axisStep: normalizeAxisStep(state?.axisStep),
  xAxisMarker: normalizeXAxisMarker(state?.xAxisMarker),
  showAxisLabels: state?.showAxisLabels ?? true,
  showAxisMarkers: state?.showAxisMarkers ?? true,
  showCanvasToolbar: state?.showCanvasToolbar ?? true,
  showHandwritingGuides: state?.showHandwritingGuides ?? true,
  canvasBackgroundColor: normalizeCanvasBackgroundColor(state?.canvasBackgroundColor),
  gridMode: state?.gridMode || 'cartesian',
  equationType: equationTypeForGroup(
    normalizeEquationGroup(state?.equationGroup || equationGroupForType(normalizeEquationType(state?.equationType))),
    normalizeEquationType(state?.equationType)
  ),
  equationGroup: normalizeEquationGroup(state?.equationGroup || equationGroupForType(normalizeEquationType(state?.equationType))),
  equationParams: state?.equationParams ? cloneValue(state.equationParams) : makeParams(normalizeEquationType(state?.equationType)),
  equationTitle: state?.equationTitle || '',
  setupMode: state?.setupMode || 'equation',
  tableTitle: state?.tableTitle || 'Table Plot',
  tableRows: Array.isArray(state?.tableRows) ? cloneValue(state.tableRows) : cloneValue(TABLE_ROWS),
  selectedGraphId: state?.selectedGraphId || '',
  compareGraphId: state?.compareGraphId || '',
  analysisMode: state?.analysisMode || '',
  activePanel: state?.activePanel || 'left',
  activeSurface: state?.activeSurface || 'graph',
  zoomLevel: Number.isFinite(state?.zoomLevel) ? state.zoomLevel : 1,
  annotationTool: state?.annotationTool || 'pointer',
  annotationStrokes: Array.isArray(state?.annotationStrokes) ? cloneValue(state.annotationStrokes).map(normalizeAnnotationItem) : [],
  annotationFuture: Array.isArray(state?.annotationFuture) ? cloneValue(state.annotationFuture).map(normalizeAnnotationItem) : [],
  handwritingStrokes: Array.isArray(state?.handwritingStrokes) ? cloneValue(state.handwritingStrokes) : [],
  handwritingFuture: Array.isArray(state?.handwritingFuture) ? cloneValue(state.handwritingFuture) : [],
  handwritingOffset: state?.handwritingOffset ? cloneValue(state.handwritingOffset) : { x: 0, y: 0 },
  annotationTextSize: state?.annotationTextSize || 24,
  annotationTextMode: state?.annotationTextMode || 'plain',
  transform: state?.transform ? cloneValue(state.transform) : defaultTransformState(),
});

const createCanvasSlide = (index, state = {}) => ({
  id: createSlideId(),
  title: `${DEFAULT_SLIDE_TITLE_PREFIX} ${index}`,
  state: normalizeCanvasState(state),
});

const readLocalSketches = () => {
  if (typeof window === 'undefined') return [];

  try {
    const raw = window.localStorage.getItem(LOCAL_GRAPH_SKETCHES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeLocalSketches = (sketches) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LOCAL_GRAPH_SKETCHES_KEY, JSON.stringify(sketches));
};

const graphUsesTrigEquation = (graph, graphMap, depth = 0) => {
  if (!graph || depth > 5) return false;
  if (graph.kind === 'function') return isTrigEquation(graph.equationType);
  if (graph.sourceGraphId) return graphUsesTrigEquation(graphMap.get(graph.sourceGraphId), graphMap, depth + 1);
  return false;
};

const constrainViewportToXDomain = (renderViewport, domainViewport) => {
  const domainXMin = Math.min(domainViewport.xMin, domainViewport.xMax);
  const domainXMax = Math.max(domainViewport.xMin, domainViewport.xMax);
  const xMin = Math.max(renderViewport.xMin, domainXMin);
  const xMax = Math.min(renderViewport.xMax, domainXMax);
  if (xMax <= xMin) return null;
  return {
    ...renderViewport,
    xMin,
    xMax,
  };
};

const sampleSegments = (graph, viewport, graphMap) => {
  const straightLine = resolveStraightLineGraph(graph, graphMap);
  if (straightLine?.orientation === 'vertical') {
    return [[
      { x: straightLine.value, y: viewport.yMin },
      { x: straightLine.value, y: viewport.yMax },
    ]];
  }
  if (straightLine?.orientation === 'horizontal') {
    return [[
      { x: viewport.xMin, y: straightLine.value },
      { x: viewport.xMax, y: straightLine.value },
    ]];
  }
  if (graph.kind === 'inverse') {
    const source = graphMap.get(graph.sourceGraphId);
    if (!source) return [];
    const sampleViewport = {
      xMin: Math.min(viewport.xMin, viewport.yMin),
      xMax: Math.max(viewport.xMax, viewport.yMax),
      yMin: Math.min(viewport.xMin, viewport.yMin),
      yMax: Math.max(viewport.xMax, viewport.yMax),
    };
    return sampleSegments(source, sampleViewport, graphMap).map((segment) =>
      segment.map((point) => ({ x: point.y, y: point.x }))
    );
  }
  if (graph.kind === 'table') {
    return [[...graph.points].sort((a, b) => a.x - b.x)];
  }
  const segments = [];
  let segment = [];
  for (let i = 0; i <= SAMPLES; i += 1) {
    const x = viewport.xMin + (i / SAMPLES) * (viewport.xMax - viewport.xMin);
    const y = evalGraph(graph, x, graphMap);
    const valid = Number.isFinite(y) && Math.abs(y) <= (viewport.yMax - viewport.yMin) * 6;
    if (!valid) {
      if (segment.length > 1) segments.push(segment);
      segment = [];
      continue;
    }
    if (segment.length > 0 && Math.abs(y - segment[segment.length - 1].y) > (viewport.yMax - viewport.yMin) * 0.6) {
      if (segment.length > 1) segments.push(segment);
      segment = [];
    }
    segment.push({ x, y });
  }
  if (segment.length > 1) segments.push(segment);
  return segments;
};

const distanceToSegment = (point, start, end) => {
  const lengthSquared = (end.x - start.x) ** 2 + (end.y - start.y) ** 2;
  if (!lengthSquared) return Math.hypot(point.x - start.x, point.y - start.y);
  let t =
    ((point.x - start.x) * (end.x - start.x) + (point.y - start.y) * (end.y - start.y)) /
    lengthSquared;
  t = Math.max(0, Math.min(1, t));
  const projection = {
    x: start.x + t * (end.x - start.x),
    y: start.y + t * (end.y - start.y),
  };
  return Math.hypot(point.x - projection.x, point.y - projection.y);
};

const findStrokeAtPoint = (strokes, point) => {
  for (let index = strokes.length - 1; index >= 0; index -= 1) {
    const stroke = strokes[index];
    if (!stroke || !Array.isArray(stroke.points) || stroke.points.length === 0) continue;
    const radius = stroke.tool === 'highlighter' ? 12 : 8;
    if (stroke.points.length === 1) {
      const onlyPoint = stroke.points[0];
      if (Math.hypot(point.x - onlyPoint.x, point.y - onlyPoint.y) <= radius) {
        return stroke.id;
      }
      continue;
    }
    for (let pointIndex = 1; pointIndex < stroke.points.length; pointIndex += 1) {
      const start = stroke.points[pointIndex - 1];
      const end = stroke.points[pointIndex];
      if (distanceToSegment(point, start, end) <= radius) {
        return stroke.id;
      }
    }
  }
  return null;
};

function LatexText({ latex, className = '' }) {
  const markup = useMemo(() => renderLatexMarkup(latex), [latex]);
  return <span className={className} dangerouslySetInnerHTML={{ __html: markup }} />;
}

function InlineLatexRenderer({ latex, className = '' }) {
  return (
    <MathRenderer
      content={`$${latex || ''}$`}
      className={className}
      enableMarkdown={false}
      enableMath
    />
  );
}

function GraphSketchTool({
  fullScreen = false,
  showSaveBar = true,
  openLibrarySignal = 0,
  newCanvasSignal = 0,
  openSchemaImportSignal = 0,
}) {
  const user = useAuthStore((state) => state.user);
  const userData = useAuthStore((state) => state.userData);
  const initialSlideDeck = useMemo(() => {
    const firstSlide = createCanvasSlide(1);
    return {
      slides: [firstSlide],
      activeSlideId: firstSlide.id,
    };
  }, []);
  const fullScreenRootRef = useRef(null);
  const canvasRef = useRef(null);
  const annotationCanvasRef = useRef(null);
  const canvasWrapRef = useRef(null);
  const handwritingCanvasRef = useRef(null);
  const handwritingWrapRef = useRef(null);
  const textInputRef = useRef(null);
  const mathCacheRef = useRef(new Map());
  const mathCssTextPromiseRef = useRef(null);
  const mathFontDataUrlCacheRef = useRef(new Map());
  const imageCacheRef = useRef(new Map());
  const annotationRef = useRef({ drawing: false, currentStroke: null });
  const handwritingRef = useRef({ drawing: false, currentStroke: null });
  const [canvasSize, setCanvasSize] = useState({ width: 960, height: 620 });
  const [handwritingSize, setHandwritingSize] = useState({ width: 320, height: 620 });
  const [graphs, setGraphs] = useState([]);
  const [viewport, setViewport] = useState(DEFAULT_VIEWPORT);
  const [axisStep, setAxisStep] = useState(1);
  const [xAxisMarker, setXAxisMarker] = useState('number');
  const [showAxisLabels, setShowAxisLabels] = useState(true);
  const [showAxisMarkers, setShowAxisMarkers] = useState(true);
  const [showCanvasToolbar, setShowCanvasToolbar] = useState(true);
  const [showHandwritingGuides, setShowHandwritingGuides] = useState(true);
  const [canvasBackgroundColor, setCanvasBackgroundColor] = useState(DEFAULT_CANVAS_BACKGROUND_COLOR);
  const [canvasBackgroundInput, setCanvasBackgroundInput] = useState(DEFAULT_CANVAS_BACKGROUND_COLOR);
  const [gridMode, setGridMode] = useState('cartesian');
  const [viewOptionsOpen, setViewOptionsOpen] = useState(false);
  const [equationGroup, setEquationGroup] = useState('normal');
  const [equationType, setEquationType] = useState('linear');
  const [equationDropdownOpen, setEquationDropdownOpen] = useState(false);
  const [equationParams, setEquationParams] = useState(makeParams('linear'));
  const [equationTitle, setEquationTitle] = useState('');
  const [setupMode, setSetupMode] = useState('equation');
  const [tableTitle, setTableTitle] = useState('Table Plot');
  const [tableRows, setTableRows] = useState(TABLE_ROWS);
  const [graphDropdownOpen, setGraphDropdownOpen] = useState(false);
  const [selectedGraphId, setSelectedGraphId] = useState('');
  const [compareGraphId, setCompareGraphId] = useState('');
  const [analysisMode, setAnalysisMode] = useState('');
  const [activePanel, setActivePanel] = useState('left');
  const [teachingMode, setTeachingMode] = useState(false);
  const [activeSurface, setActiveSurface] = useState('graph');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [annotationTool, setAnnotationTool] = useState('pointer');
  const [annotationStrokes, setAnnotationStrokes] = useState([]);
  const [annotationFuture, setAnnotationFuture] = useState([]);
  const [handwritingStrokes, setHandwritingStrokes] = useState([]);
  const [handwritingFuture, setHandwritingFuture] = useState([]);
  const [handwritingOffset, setHandwritingOffset] = useState({ x: 0, y: 0 });
  const [annotationTextSize, setAnnotationTextSize] = useState(24);
  const [annotationTextMode, setAnnotationTextMode] = useState('plain');
  const [textDraft, setTextDraft] = useState(null);
  const [mathDraft, setMathDraft] = useState(null);
  const [selectionRect, setSelectionRect] = useState(null);
  const [selectedAnnotationItemId, setSelectedAnnotationItemId] = useState('');
  const [selectionMenu, setSelectionMenu] = useState(null);
  const [selectionCopyFeedback, setSelectionCopyFeedback] = useState({ type: 'idle', message: '' });
  const [selectionResizeCursor, setSelectionResizeCursor] = useState('');
  const [transform, setTransform] = useState(defaultTransformState);
  const [slides, setSlides] = useState(initialSlideDeck.slides);
  const [activeSlideId, setActiveSlideId] = useState(initialSlideDeck.activeSlideId);
  const [sketchTitle, setSketchTitle] = useState(DEFAULT_SKETCH_TITLE);
  const [currentSketchId, setCurrentSketchId] = useState('');
  const [savedSketches, setSavedSketches] = useState([]);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [isLibraryLoading, setIsLibraryLoading] = useState(false);
  const [isSavingSketch, setIsSavingSketch] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState({ type: 'idle', message: '' });
  const [isSchemaImportOpen, setIsSchemaImportOpen] = useState(false);
  const [schemaInput, setSchemaInput] = useState('');
  const [schemaErrors, setSchemaErrors] = useState([]);
  const [schemaTemplateCopied, setSchemaTemplateCopied] = useState(false);

  const graphMap = useMemo(() => new Map(graphs.map((graph) => [graph.id, graph])), [graphs]);
  const selectedGraph = selectedGraphId ? graphMap.get(selectedGraphId) || null : null;
  const compareGraph = compareGraphId ? graphMap.get(compareGraphId) || null : null;
  const xAxisMarkerOption = getXAxisMarkerOption(xAxisMarker);
  const xAxisStep = xAxisMarkerOption.degreeStep || axisStep;
  const renderMetrics = useMemo(() => getRenderMetrics(viewport, canvasSize, zoomLevel, axisStep, xAxisStep), [viewport, canvasSize, zoomLevel, axisStep, xAxisStep]);
  const visibleViewport = renderMetrics.viewport;
  const activeSlide = useMemo(
    () => slides.find((slide) => slide.id === activeSlideId) || slides[0] || null,
    [activeSlideId, slides]
  );
  const activeSlideIndex = useMemo(
    () => slides.findIndex((slide) => slide.id === activeSlideId),
    [activeSlideId, slides]
  );
  const selectedAnnotationItem = useMemo(
    () => annotationStrokes.find((item) => item?.id === selectedAnnotationItemId) || null,
    [annotationStrokes, selectedAnnotationItemId]
  );

  const selectedTextSizeId = TEXT_SIZE_OPTIONS.find((option) => option.size === annotationTextSize)?.id || 'M';
  const showGridLines = gridMode === 'cartesian';
  const activeUndoDisabled = activeSurface === 'handwriting' ? !handwritingStrokes.length : !annotationStrokes.length;
  const activeRedoDisabled = activeSurface === 'handwriting' ? !handwritingFuture.length : !annotationFuture.length;

  useEffect(() => {
    if (selectionCopyFeedback.type === 'idle') return undefined;
    const timer = window.setTimeout(() => {
      setSelectionCopyFeedback({ type: 'idle', message: '' });
    }, 1800);
    return () => window.clearTimeout(timer);
  }, [selectionCopyFeedback]);

  const applyCanvasState = useCallback((state, options = {}) => {
    const { preserveTeachingMode = false } = options;
    const nextState = normalizeCanvasState(state);
    setGraphs(nextState.graphs);
    setViewport(nextState.viewport);
    setAxisStep(nextState.axisStep);
    setXAxisMarker(nextState.xAxisMarker);
    setShowAxisLabels(nextState.showAxisLabels);
    setShowAxisMarkers(nextState.showAxisMarkers);
    setShowCanvasToolbar(nextState.showCanvasToolbar);
    setShowHandwritingGuides(nextState.showHandwritingGuides);
    setCanvasBackgroundColor(nextState.canvasBackgroundColor);
    setCanvasBackgroundInput(nextState.canvasBackgroundColor);
    setGridMode(nextState.gridMode);
    setViewOptionsOpen(false);
    setEquationGroup(nextState.equationGroup);
    setEquationType(nextState.equationType);
    setEquationDropdownOpen(false);
    setEquationParams(nextState.equationParams);
    setEquationTitle(nextState.equationTitle);
    setSetupMode(nextState.setupMode);
    setTableTitle(nextState.tableTitle);
    setTableRows(nextState.tableRows);
    setGraphDropdownOpen(false);
    setSelectedGraphId(nextState.selectedGraphId);
    setCompareGraphId(nextState.compareGraphId);
    setAnalysisMode(nextState.analysisMode);
    setActivePanel(nextState.activePanel);
    if (!preserveTeachingMode) setTeachingMode(false);
    setActiveSurface(nextState.activeSurface);
    setZoomLevel(nextState.zoomLevel);
    setAnnotationTool(nextState.annotationTool);
    setAnnotationStrokes(nextState.annotationStrokes);
    setAnnotationFuture(nextState.annotationFuture);
    setHandwritingStrokes(nextState.handwritingStrokes);
    setHandwritingFuture(nextState.handwritingFuture);
    setHandwritingOffset(nextState.handwritingOffset);
    setAnnotationTextSize(nextState.annotationTextSize);
    setAnnotationTextMode(nextState.annotationTextMode);
    setTextDraft(null);
    setMathDraft(null);
    setSelectionRect(null);
    setSelectedAnnotationItemId('');
    setSelectionMenu(null);
    setTransform(nextState.transform);
  }, []);

  const buildCanvasState = useCallback(() => ({
    graphs: cloneValue(graphs),
    viewport: cloneValue(viewport),
    axisStep,
    xAxisMarker,
    showAxisLabels,
    showAxisMarkers,
    showCanvasToolbar,
    showHandwritingGuides,
    canvasBackgroundColor,
    gridMode,
    equationGroup,
    equationType,
    equationParams: cloneValue(equationParams),
    equationTitle,
    setupMode,
    tableTitle,
    tableRows: cloneValue(tableRows),
    selectedGraphId,
    compareGraphId,
    analysisMode,
    activePanel,
    activeSurface,
    zoomLevel,
    annotationTool,
    annotationStrokes: cloneValue(annotationStrokes),
    annotationFuture: cloneValue(annotationFuture),
    handwritingStrokes: cloneValue(handwritingStrokes),
    handwritingFuture: cloneValue(handwritingFuture),
    handwritingOffset: cloneValue(handwritingOffset),
    annotationTextSize,
    annotationTextMode,
    transform: cloneValue(transform),
  }), [
    activePanel,
    activeSurface,
    analysisMode,
    annotationFuture,
    annotationStrokes,
    annotationTextMode,
    annotationTextSize,
    annotationTool,
    axisStep,
    canvasBackgroundColor,
    compareGraphId,
    equationGroup,
    equationParams,
    equationTitle,
    equationType,
    graphs,
    gridMode,
    handwritingFuture,
    handwritingOffset,
    handwritingStrokes,
    selectedGraphId,
    setupMode,
    showAxisLabels,
    showAxisMarkers,
    showCanvasToolbar,
    showHandwritingGuides,
    tableRows,
    tableTitle,
    transform,
    viewport,
    xAxisMarker,
    zoomLevel,
  ]);

  const buildSlidesSnapshot = useCallback(() => {
    const currentCanvasState = buildCanvasState();
    const nextSlides = (slides.length ? slides : [createCanvasSlide(1)]).map((slide, index) => ({
      id: slide?.id || createSlideId(),
      title: slide?.title?.trim() || `${DEFAULT_SLIDE_TITLE_PREFIX} ${index + 1}`,
      state: slide?.id === activeSlideId
        ? currentCanvasState
        : normalizeCanvasState(slide?.state || slide),
    }));
    const nextActiveSlideId = nextSlides.some((slide) => slide.id === activeSlideId)
      ? activeSlideId
      : nextSlides[0]?.id || '';
    return {
      slides: nextSlides,
      activeSlideId: nextActiveSlideId,
      currentCanvasState,
    };
  }, [activeSlideId, buildCanvasState, slides]);

  const applySketchState = useCallback((state) => {
    const rawSlides = Array.isArray(state?.slides) && state.slides.length
      ? state.slides
      : [{ id: createSlideId(), title: `${DEFAULT_SLIDE_TITLE_PREFIX} 1`, state }];
    const normalizedSlides = rawSlides.map((slide, index) => ({
      id: slide?.id || createSlideId(),
      title: slide?.title?.trim() || `${DEFAULT_SLIDE_TITLE_PREFIX} ${index + 1}`,
      state: normalizeCanvasState(slide?.state || slide),
    }));
    const nextActiveSlideId = normalizedSlides.some((slide) => slide.id === state?.activeSlideId)
      ? state.activeSlideId
      : normalizedSlides[0]?.id || '';
    const nextActiveSlide = normalizedSlides.find((slide) => slide.id === nextActiveSlideId) || normalizedSlides[0];

    setSlides(normalizedSlides);
    setActiveSlideId(nextActiveSlideId);
    applyCanvasState(nextActiveSlide?.state || normalizeCanvasState());
  }, [applyCanvasState]);

  const openSchemaImporter = useCallback(() => {
    setSchemaInput('');
    setSchemaErrors([]);
    setSchemaTemplateCopied(false);
    setIsSchemaImportOpen(true);
  }, []);

  const closeSchemaImporter = useCallback(() => {
    setIsSchemaImportOpen(false);
    setSchemaErrors([]);
  }, []);

  const importSchemaDraft = useCallback(() => {
    const result = parseGraphSketchDocumentSchema(schemaInput);
    if (!result.ok) {
      setSchemaErrors(result.errors);
      return;
    }

    applySketchState(result.value.sketchData);
    setCurrentSketchId('');
    setSketchTitle(result.value.title);
    setIsLibraryOpen(false);
    setIsSchemaImportOpen(false);
    setSchemaInput('');
    setSchemaErrors([]);
    setSaveFeedback({
      type: 'idle',
      message: 'Schema imported as an unsaved draft. Click Save to create a new graph document.',
    });
  }, [applySketchState, schemaInput]);

  const copySchemaTemplate = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(GRAPH_SKETCH_SCHEMA_EXAMPLE);
      setSchemaTemplateCopied(true);
    } catch {
      setSchemaErrors([{ path: '$', message: 'Could not copy the schema template to the clipboard.' }]);
    }
  }, []);

  const serializeSketchState = useCallback(() => {
    const { slides: nextSlides, activeSlideId: nextActiveSlideId, currentCanvasState } = buildSlidesSnapshot();
    return {
      version: 2,
      ...currentCanvasState,
      slides: cloneValue(nextSlides),
      activeSlideId: nextActiveSlideId,
    };
  }, [buildSlidesSnapshot]);

  const loadSavedSketches = useCallback(async () => {
    setIsLibraryLoading(true);
    try {
      if (user?.uid) {
        const sketches = await FirestoreService.getUserGraphSketches(user.uid);
        setSavedSketches(sketches.map((sketch) => ({
          id: sketch.id,
          title: sketch.title || DEFAULT_SKETCH_TITLE,
          updatedAt: sketch.updatedAt?.toDate?.() || new Date(),
        })));
      } else {
        setSavedSketches(
          readLocalSketches()
            .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
            .map((sketch) => ({
              id: sketch.id,
              title: sketch.title || DEFAULT_SKETCH_TITLE,
              updatedAt: new Date(sketch.updatedAt || Date.now()),
            }))
        );
      }
    } catch (error) {
      console.error('Failed to load graph sketches:', error);
      setSaveFeedback({ type: 'error', message: error.message || 'Failed to load graph sketches.' });
      setSavedSketches([]);
    } finally {
      setIsLibraryLoading(false);
    }
  }, [user?.uid]);

  const loadSketch = useCallback(async (sketchId) => {
    try {
      if (user?.uid) {
        const sketch = await FirestoreService.getGraphSketch(sketchId);
        applySketchState(sketch.sketchData || {});
        setCurrentSketchId(sketch.id);
        setSketchTitle(sketch.title || DEFAULT_SKETCH_TITLE);
      } else {
        const sketch = readLocalSketches().find((item) => item.id === sketchId);
        if (!sketch) throw new Error('Saved graph sketch not found.');
        applySketchState(sketch.sketchData || {});
        setCurrentSketchId(sketch.id);
        setSketchTitle(sketch.title || DEFAULT_SKETCH_TITLE);
      }

      setSaveFeedback({ type: 'saved', message: 'Graph sketch loaded.' });
      setIsLibraryOpen(false);
    } catch (error) {
      console.error('Failed to load graph sketch:', error);
      setSaveFeedback({ type: 'error', message: error.message || 'Failed to load graph sketch.' });
    }
  }, [applySketchState, user?.uid]);

  const deleteSketch = useCallback(async (sketchId) => {
    try {
      if (user?.uid) {
        await FirestoreService.deleteGraphSketch(sketchId);
      } else {
        writeLocalSketches(readLocalSketches().filter((sketch) => sketch.id !== sketchId));
      }

      if (currentSketchId === sketchId) {
        setCurrentSketchId('');
        setSketchTitle(DEFAULT_SKETCH_TITLE);
      }

      await loadSavedSketches();
      setSaveFeedback({ type: 'saved', message: 'Graph sketch deleted.' });
    } catch (error) {
      console.error('Failed to delete graph sketch:', error);
      setSaveFeedback({ type: 'error', message: error.message || 'Failed to delete graph sketch.' });
    }
  }, [currentSketchId, loadSavedSketches, user?.uid]);

  const saveSketch = useCallback(async (options = {}) => {
    const { saveAsNew = false } = options;
    const nextTitle = (sketchTitle || '').trim() || DEFAULT_SKETCH_TITLE;
    const sketchData = serializeSketchState();

    if (!canSaveToolWork(userData)) {
      setSaveFeedback({ type: 'error', message: getToolSaveMessage() });
      return;
    }

    setIsSavingSketch(true);
    setSaveFeedback({ type: 'saving', message: 'Saving graph sketch...' });

    try {
      if (user?.uid) {
        if (!saveAsNew && currentSketchId) {
          await FirestoreService.updateGraphSketch(currentSketchId, {
            title: nextTitle,
            status: 'draft',
            sketchData,
          });
        } else {
          const createdId = await FirestoreService.createGraphSketch(user.uid, {
            title: nextTitle,
            status: 'draft',
            sketchData,
          });
          setCurrentSketchId(createdId);
        }
      } else {
        const sketches = readLocalSketches();
        const sketchId = !saveAsNew && currentSketchId ? currentSketchId : createLocalSketchId();
        const createdAt = (!saveAsNew && currentSketchId
          ? sketches.find((sketch) => sketch.id === sketchId)?.createdAt
          : null) || new Date().toISOString();
        const nextSketch = {
          id: sketchId,
          title: nextTitle,
          sketchData,
          createdAt,
          updatedAt: new Date().toISOString(),
        };
        writeLocalSketches([nextSketch, ...sketches.filter((sketch) => sketch.id !== sketchId)]);
        setCurrentSketchId(sketchId);
      }

      setSketchTitle(nextTitle);
      setSaveFeedback({
        type: 'saved',
        message: user?.uid ? 'Graph sketch saved to your account.' : 'Graph sketch saved in this browser.',
      });
      await loadSavedSketches();
    } catch (error) {
      console.error('Failed to save graph sketch:', error);
      setSaveFeedback({ type: 'error', message: error.message || 'Failed to save graph sketch.' });
    } finally {
      setIsSavingSketch(false);
    }
  }, [currentSketchId, loadSavedSketches, serializeSketchState, sketchTitle, user?.uid, userData]);

  const switchToSlide = useCallback((slideId) => {
    if (!slideId || slideId === activeSlideId) return;
    const { slides: nextSlides } = buildSlidesSnapshot();
    const targetSlide = nextSlides.find((slide) => slide.id === slideId);
    if (!targetSlide) return;
    setSlides(nextSlides);
    setActiveSlideId(targetSlide.id);
    applyCanvasState(targetSlide.state, { preserveTeachingMode: true });
  }, [activeSlideId, applyCanvasState, buildSlidesSnapshot]);

  const addSlide = useCallback(() => {
    const { slides: nextSlides } = buildSlidesSnapshot();
    const nextSlide = createCanvasSlide(nextSlides.length + 1);
    setSlides([...nextSlides, nextSlide]);
    setActiveSlideId(nextSlide.id);
    applyCanvasState(nextSlide.state, { preserveTeachingMode: true });
  }, [applyCanvasState, buildSlidesSnapshot]);

  const duplicateSlide = useCallback(() => {
    const { slides: nextSlides, activeSlideId: nextActiveSlideId } = buildSlidesSnapshot();
    const sourceSlide = nextSlides.find((slide) => slide.id === nextActiveSlideId) || nextSlides[0];
    if (!sourceSlide) return;
    const duplicate = {
      id: createSlideId(),
      title: `${sourceSlide.title} copy`,
      state: cloneValue(sourceSlide.state),
    };
    setSlides([...nextSlides, duplicate]);
    setActiveSlideId(duplicate.id);
    applyCanvasState(duplicate.state, { preserveTeachingMode: true });
  }, [applyCanvasState, buildSlidesSnapshot]);

  const removeSlide = useCallback((slideId) => {
    const { slides: nextSlides, activeSlideId: nextActiveSlideId } = buildSlidesSnapshot();
    if (!nextSlides.length) return;

    if (nextSlides.length === 1) {
      const replacement = createCanvasSlide(1);
      setSlides([replacement]);
      setActiveSlideId(replacement.id);
      applyCanvasState(replacement.state, { preserveTeachingMode: true });
      return;
    }

    const removedIndex = nextSlides.findIndex((slide) => slide.id === slideId);
    const remainingSlides = nextSlides.filter((slide) => slide.id !== slideId);
    const fallbackIndex = removedIndex > 0 ? removedIndex - 1 : 0;
    const nextSlide = slideId === nextActiveSlideId
      ? remainingSlides[fallbackIndex] || remainingSlides[0]
      : remainingSlides.find((slide) => slide.id === nextActiveSlideId) || remainingSlides[0];

    setSlides(remainingSlides);
    if (nextSlide) {
      setActiveSlideId(nextSlide.id);
      applyCanvasState(nextSlide.state, { preserveTeachingMode: true });
    }
  }, [applyCanvasState, buildSlidesSnapshot]);

  const startNewCanvas = useCallback(() => {
    const firstSlide = createCanvasSlide(1);
    setSlides([firstSlide]);
    setActiveSlideId(firstSlide.id);
    applyCanvasState(firstSlide.state);
    setCurrentSketchId('');
    setSketchTitle(DEFAULT_SKETCH_TITLE);
    setIsLibraryOpen(false);
    setSaveFeedback({ type: 'idle', message: '' });
  }, [applyCanvasState]);

  useEffect(() => {
    if (!fullScreen || !teachingMode || typeof document === 'undefined') return undefined;

    const root = fullScreenRootRef.current;
    const requestFullScreen = root?.requestFullscreen?.bind(root)
      || root?.webkitRequestFullscreen?.bind(root)
      || null;

    if (!requestFullScreen) return undefined;

    requestFullScreen().catch?.(() => {});

    return () => {
      if (document.fullscreenElement === root) {
        document.exitFullscreen?.().catch?.(() => {});
      } else if (document.webkitFullscreenElement === root) {
        document.webkitExitFullscreen?.();
      }
    };
  }, [fullScreen, teachingMode]);

  const inlineFontUrlsInCss = useCallback(async (cssText, baseHref) => {
    const matches = Array.from(cssText.matchAll(CSS_URL_PATTERN));
    if (!matches.length) return cssText;

    let nextCssText = cssText;
    await Promise.all(matches.map(async (match) => {
      const rawUrl = match[2];
      if (!rawUrl || /^(data:|blob:|#)/i.test(rawUrl)) return;

      let absoluteUrl;
      try {
        absoluteUrl = new URL(rawUrl, baseHref || document.baseURI).toString();
      } catch {
        return;
      }

      try {
        let dataUrl = mathFontDataUrlCacheRef.current.get(absoluteUrl);
        if (!dataUrl) {
          const response = await fetch(absoluteUrl);
          if (!response.ok) return;
          dataUrl = await readBlobAsDataUrl(await response.blob());
          mathFontDataUrlCacheRef.current.set(absoluteUrl, dataUrl);
        }
        nextCssText = nextCssText.replace(match[0], `url("${dataUrl}")`);
      } catch {
        // Keep the original URL if a font cannot be fetched; MathLive can still fall back.
      }
    }));

    return nextCssText;
  }, []);

  const getMathCssText = useCallback(async () => {
    if (mathCssTextPromiseRef.current) {
      return mathCssTextPromiseRef.current;
    }

    mathCssTextPromiseRef.current = (async () => {
      const cssRules = [];

      await Promise.all(Array.from(document.styleSheets).map(async (sheet) => {
        let rules;
        try {
          rules = sheet.cssRules;
        } catch {
          rules = null;
        }

        if (!rules) return;

        await Promise.all(Array.from(rules).map(async (rule) => {
          const cssText = rule.cssText || '';
          const isFontFaceRule = rule.type === window.CSSRule?.FONT_FACE_RULE;
          if (
            isFontFaceRule ||
            cssText.includes('.ML__') ||
            cssText.includes('math-field') ||
            cssText.includes(':root')
          ) {
            cssRules.push(isFontFaceRule ? await inlineFontUrlsInCss(cssText, sheet.href) : cssText);
          }
        }));
      }));

      return cssRules.join('\n');
    })();

    return mathCssTextPromiseRef.current;
  }, [inlineFontUrlsInCss]);

  const buildMathAsset = useCallback(async (latex, size) => {
    const cacheKey = `${latex}__${size}`;
    const cached = mathCacheRef.current.get(cacheKey);
    if (cached?.ready) {
      return cached;
    }

    const measureEl = document.createElement('div');
    measureEl.className = 'math-container graph-math-measure';
    measureEl.contentEditable = 'false';
    measureEl.style.position = 'fixed';
    measureEl.style.left = '-10000px';
    measureEl.style.top = '0';
    measureEl.style.visibility = 'hidden';
    measureEl.style.pointerEvents = 'none';
    measureEl.style.display = 'inline-block';
    measureEl.style.verticalAlign = 'baseline';
    measureEl.style.lineHeight = 'normal';
    measureEl.style.whiteSpace = 'nowrap';
    measureEl.style.color = '#0F172A';
    measureEl.style.fontSize = `${size}px`;
    measureEl.innerHTML = `\\(${latex}\\)`;
    document.body.appendChild(measureEl);

    try {
      const { renderMathInElement } = await import('mathlive');
      renderMathInElement(measureEl, { renderAccessibleContent: false });
    } catch {
      measureEl.innerHTML = convertLatexToMarkup(latex, { defaultMode: 'inline-math' });
    }

    await document.fonts?.ready;

    const rect = measureEl.getBoundingClientRect();
    const width = Math.max(12, Math.ceil(rect.width) + 6);
    const height = Math.max(12, Math.ceil(rect.height) + 6);
    const markup = measureEl.innerHTML;
    document.body.removeChild(measureEl);

    const cssText = await getMathCssText();
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
        <foreignObject width="100%" height="100%">
          <style>${cssText}</style>
          <div xmlns="http://www.w3.org/1999/xhtml" style="display:inline-block;padding:3px;color:#0F172A;font-size:${size}px;line-height:normal;white-space:nowrap;">
            ${markup}
          </div>
        </foreignObject>
      </svg>
    `;

    const src = encodeSvg(svg);
    const image = new Image();
    const entry = { ready: false, src, image, width, height };
    image.onload = () => {
      entry.ready = true;
      setAnnotationStrokes((current) => [...current]);
    };
    image.onerror = () => {
      entry.ready = true;
    };
    image.src = src;
    mathCacheRef.current.set(cacheKey, entry);
    return entry;
  }, [getMathCssText]);

  const ensureImageAsset = useCallback((src) => {
    if (!src) return null;
    const cached = imageCacheRef.current.get(src);
    if (cached) return cached;
    const image = new Image();
    const entry = { ready: false, image };
    image.onload = () => {
      entry.ready = true;
      setAnnotationStrokes((current) => [...current]);
    };
    image.onerror = () => {
      entry.ready = true;
    };
    image.src = src;
    imageCacheRef.current.set(src, entry);
    return entry;
  }, []);

  const readFileAsDataUrl = useCallback((file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error || new Error('Failed to read image.'));
    reader.readAsDataURL(file);
  }), []);

  const loadImageSize = useCallback((src) => new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      resolve({
        width: image.naturalWidth || image.width,
        height: image.naturalHeight || image.height,
      });
    };
    image.onerror = () => reject(new Error('Failed to load image.'));
    image.src = src;
  }), []);

  const addImageToCanvas = useCallback(async (file, dropPoint = null) => {
    if (!file?.type?.startsWith('image/')) return false;
    const src = await readFileAsDataUrl(file);
    if (!src) return false;
    const naturalSize = await loadImageSize(src);
    const maxWidth = Math.max(MIN_IMAGE_SIZE, Math.floor(canvasSize.width * 0.6));
    const maxHeight = Math.max(MIN_IMAGE_SIZE, Math.floor(canvasSize.height * 0.6));
    const fittedSize = fitImageToCanvas(naturalSize.width, naturalSize.height, maxWidth, maxHeight);
    const targetPoint = dropPoint || { x: canvasSize.width / 2, y: canvasSize.height / 2 };
    const imageItem = {
      id: `image-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      kind: 'image',
      src,
      canvasPlacement: 'front',
      x: clamp(targetPoint.x - fittedSize.width / 2, 0, Math.max(0, canvasSize.width - fittedSize.width)),
      y: clamp(targetPoint.y - fittedSize.height / 2, 0, Math.max(0, canvasSize.height - fittedSize.height)),
      width: fittedSize.width,
      height: fittedSize.height,
      aspectRatio: naturalSize.width > 0 && naturalSize.height > 0 ? naturalSize.width / naturalSize.height : 1,
    };
    ensureImageAsset(src);
    setAnnotationFuture([]);
    setSelectionRect(null);
    setSelectionMenu(null);
    setSelectedAnnotationItemId(imageItem.id);
    setAnnotationTool('pointer');
    setActiveSurface('graph');
    setAnnotationStrokes((current) => [...current, imageItem]);
    return true;
  }, [canvasSize.height, canvasSize.width, ensureImageAsset, loadImageSize, readFileAsDataUrl]);

  const activeGraphDefinition = useMemo(() => {
    if (!selectedGraph) return 'Choose a graph to inspect its definition.';
    if (selectedGraph.kind === 'function') {
      return functionGraphLatex(selectedGraph);
    }
    if (selectedGraph.kind === 'table') {
      return `\\text{Table plot with }${selectedGraph.points.length}\\text{ point${selectedGraph.points.length === 1 ? '' : 's'}}`;
    }
    if (selectedGraph.kind === 'transform') {
      return `f(x+${fmt(selectedGraph.horizontalShift)})+${fmt(selectedGraph.verticalShift)},\\; -f(x):${selectedGraph.reflectX ? '\\text{yes}' : '\\text{no}'},\\; f(-x):${selectedGraph.reflectY ? '\\text{yes}' : '\\text{no}'}`;
    }
    if (selectedGraph.kind === 'derivative') {
      const source = graphMap.get(selectedGraph.sourceGraphId);
      return derivativeLatex(source, graphMap);
    }
    if (selectedGraph.kind === 'inverse') {
      return `f^{-1}(x),\\; ${graphLatex(graphMap.get(selectedGraph.sourceGraphId), graphMap)}`;
    }
    return selectedGraph.title;
  }, [graphMap, selectedGraph]);

  const derivativeInsightLatex = useMemo(() => {
    if (!selectedGraph) return "f'(x)";
    return derivativeLatex(selectedGraph, graphMap);
  }, [graphMap, selectedGraph]);

  const inverseInsightLatex = useMemo(() => {
    if (!selectedGraph) return 'f^{-1}(x)';
    if (selectedGraph.kind === 'inverse') return 'f^{-1}(x)';
    return `${graphLatex(selectedGraph, graphMap)}^{-1}`;
  }, [graphMap, selectedGraph]);

  useEffect(() => {
    if (!equationDropdownOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!event.target.closest('[data-equation-dropdown="root"]')) {
        setEquationDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [equationDropdownOpen]);

  useEffect(() => {
    if (!graphDropdownOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!event.target.closest('[data-graph-dropdown="root"]')) {
        setGraphDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [graphDropdownOpen]);

  useEffect(() => {
    if (!viewOptionsOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!event.target.closest('[data-view-options-root]')) {
        setViewOptionsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [viewOptionsOpen]);

  useEffect(() => {
    const resize = () => {
      if (!canvasWrapRef.current) return;
      const styles = window.getComputedStyle(canvasWrapRef.current);
      const horizontalPadding = num(styles.paddingLeft) + num(styles.paddingRight);
      const verticalPadding = num(styles.paddingTop) + num(styles.paddingBottom);
      const nextWidth = Math.max(320, Math.floor(canvasWrapRef.current.clientWidth - horizontalPadding));
      const nextHeight = Math.max(
        fullScreen ? 540 : 500,
        Math.floor((canvasWrapRef.current.clientHeight || 560) - verticalPadding)
      );
      setCanvasSize((current) =>
        current.width === nextWidth && current.height === nextHeight
          ? current
          : { width: nextWidth, height: nextHeight }
      );
    };
    resize();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    if (observer && canvasWrapRef.current) observer.observe(canvasWrapRef.current);
    if (!observer) window.addEventListener('resize', resize);
    return () => {
      if (observer) observer.disconnect();
      else window.removeEventListener('resize', resize);
    };
  }, [fullScreen]);

  useEffect(() => {
    const resize = () => {
      if (!handwritingWrapRef.current) return;
      const styles = window.getComputedStyle(handwritingWrapRef.current);
      const horizontalPadding = num(styles.paddingLeft) + num(styles.paddingRight);
      const verticalPadding = num(styles.paddingTop) + num(styles.paddingBottom);
      const nextWidth = Math.max(220, Math.floor(handwritingWrapRef.current.clientWidth - horizontalPadding));
      const nextHeight = Math.max(320, Math.floor((handwritingWrapRef.current.clientHeight || 560) - verticalPadding));
      setHandwritingSize((current) =>
        current.width === nextWidth && current.height === nextHeight
          ? current
          : { width: nextWidth, height: nextHeight }
      );
    };
    resize();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    if (observer && handwritingWrapRef.current) observer.observe(handwritingWrapRef.current);
    if (!observer) window.addEventListener('resize', resize);
    return () => {
      if (observer) observer.disconnect();
      else window.removeEventListener('resize', resize);
    };
  }, [teachingMode]);

  const summary = useMemo(() => {
    if (!selectedGraph) return 'Select a graph to analyse.';
    if (!analysisMode) return 'Choose an insight to highlight intervals or compare behavior in the current view.';
    const selected = (x) => evalGraph(selectedGraph, x, graphMap);
    const compared = (x) => evalGraph(compareGraph, x, graphMap);
    if (analysisMode === 'positive') return formatIntervals(intervalsFor((x) => (Number.isFinite(selected(x)) ? selected(x) > 0 : null), visibleViewport));
    if (analysisMode === 'negative') return formatIntervals(intervalsFor((x) => (Number.isFinite(selected(x)) ? selected(x) < 0 : null), visibleViewport));
    if (analysisMode === 'compare') return compareGraph ? formatIntervals(intervalsFor((x) => (Number.isFinite(selected(x)) && Number.isFinite(compared(x)) ? selected(x) > compared(x) : null), visibleViewport)) : 'Choose g(x).';
    if (analysisMode === 'increasing') return formatIntervals(intervalsFor((x) => {
      const dx = (visibleViewport.xMax - visibleViewport.xMin) / SAMPLES;
      const first = selected(x);
      const second = selected(x + dx);
      return Number.isFinite(first) && Number.isFinite(second) ? second - first > 0.001 : null;
    }, visibleViewport));
    if (analysisMode === 'decreasing') return formatIntervals(intervalsFor((x) => {
      const dx = (visibleViewport.xMax - visibleViewport.xMin) / SAMPLES;
      const first = selected(x);
      const second = selected(x + dx);
      return Number.isFinite(first) && Number.isFinite(second) ? second - first < -0.001 : null;
    }, visibleViewport));
    if (analysisMode === 'productPositive') return compareGraph ? formatIntervals(intervalsFor((x) => (Number.isFinite(selected(x)) && Number.isFinite(compared(x)) ? selected(x) * compared(x) > 0 : null), visibleViewport)) : 'Choose g(x).';
    if (analysisMode === 'quotientPositive') return compareGraph ? formatIntervals(intervalsFor((x) => {
      const first = selected(x);
      const second = compared(x);
      return Number.isFinite(first) && Number.isFinite(second) && Math.abs(second) > 1e-6 ? first / second > 0 : null;
    }, visibleViewport)) : 'Choose g(x).';
    return 'The dashed guide shows y = x. Add an inverse overlay to compare reflections.';
  }, [analysisMode, compareGraph, graphMap, selectedGraph, visibleViewport]);

  const selectedImagePlacement = selectedAnnotationItem?.kind === 'image'
    ? getImageCanvasPlacement(selectedAnnotationItem)
    : null;

  const updateSelectedImagePlacement = useCallback((canvasPlacement) => {
    if (!selectedAnnotationItemId) return;
    setAnnotationFuture([]);
    setAnnotationStrokes((current) =>
      current.map((item) =>
        item?.id === selectedAnnotationItemId && item.kind === 'image'
          ? { ...item, canvasPlacement }
          : item
      )
    );
  }, [selectedAnnotationItemId]);

  const drawAnnotationLayer = useMemo(
    () => (ctx, { includeSelection = true, includeItemSelection = true } = {}) => {
      annotationStrokes.forEach((stroke) => {
        if (!stroke) return;
        if (stroke.kind === 'text') {
          ctx.save();
          ctx.fillStyle = '#0F172A';
          ctx.font = `${stroke.size}px sans-serif`;
          ctx.textBaseline = 'top';
          ctx.fillText(stroke.text, stroke.x, stroke.y);
          ctx.restore();
          return;
        }
        if (stroke.kind === 'math') {
          const cached = mathCacheRef.current.get(`${stroke.latex}__${stroke.size}`);
          if (cached?.image?.complete) {
            ctx.drawImage(cached.image, stroke.x, stroke.y, stroke.width, stroke.height);
          }
          return;
        }
        if (stroke.kind === 'image') {
          if (isBehindCanvasImage(stroke)) return;
          const cached = ensureImageAsset(stroke.src);
          if (cached?.image?.complete) {
            ctx.drawImage(cached.image, stroke.x, stroke.y, stroke.width, stroke.height);
          }
          return;
        }
        if (['arrow', 'dot'].includes(stroke.kind)) {
          const linkedGraph = stroke.graphId ? graphMap.get(stroke.graphId) : null;
          drawMarker(ctx, linkedGraph ? {
            ...stroke,
            color: linkedGraph.color,
            lineStyle: linkedGraph.lineStyle || 'solid',
            lineWidth: linkedGraph.id === selectedGraphId ? 3 : 2,
          } : stroke);
          return;
        }
        if (!Array.isArray(stroke.points) || stroke.points.length === 0) return;
        ctx.beginPath();
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = stroke.tool === 'highlighter' ? 'rgba(250, 204, 21, 0.35)' : '#0F172A';
        ctx.lineWidth = stroke.tool === 'highlighter' ? 14 : 2.5;
        stroke.points.forEach((point, index) => {
          if (index === 0) ctx.moveTo(point.x, point.y);
          else ctx.lineTo(point.x, point.y);
        });
        ctx.stroke();
      });

      if (includeItemSelection && selectedAnnotationItem && ['text', 'math', 'image', 'arrow', 'dot'].includes(selectedAnnotationItem.kind)) {
        const bounds = selectedAnnotationItem.kind === 'text'
          ? getTextBounds(selectedAnnotationItem)
          : selectedAnnotationItem.kind === 'image'
            ? getImageBounds(selectedAnnotationItem)
            : ['arrow', 'dot'].includes(selectedAnnotationItem.kind)
              ? getMarkerBounds(selectedAnnotationItem)
            : {
              x: selectedAnnotationItem.x,
              y: selectedAnnotationItem.y,
              width: selectedAnnotationItem.width,
              height: selectedAnnotationItem.height,
            };
        ctx.save();
        ctx.strokeStyle = 'rgba(37, 99, 235, 0.95)';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([6, 4]);
        if (selectedAnnotationItem.kind === 'arrow') {
          ctx.save();
          ctx.translate(selectedAnnotationItem.x, selectedAnnotationItem.y);
          ctx.rotate(((selectedAnnotationItem.rotation || 0) * Math.PI) / 180);
          ctx.strokeRect(-(selectedAnnotationItem.length || 34) / 2 - 5, -12, (selectedAnnotationItem.length || 34) + 10, 24);
          ctx.restore();
        } else {
          ctx.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);
        }
        ctx.setLineDash([]);
        if (selectedAnnotationItem.kind === 'image') {
          const handleBounds = getImageResizeHandleBounds(bounds);
          ctx.fillStyle = '#2563EB';
          ctx.fillRect(handleBounds.x, handleBounds.y, handleBounds.width, handleBounds.height);
          ctx.strokeStyle = '#FFFFFF';
          ctx.lineWidth = 1;
          ctx.strokeRect(handleBounds.x + 0.5, handleBounds.y + 0.5, handleBounds.width - 1, handleBounds.height - 1);
        }
        if (['arrow', 'dot'].includes(selectedAnnotationItem.kind)) {
          const handles = getMarkerHandles(selectedAnnotationItem);
          if (selectedAnnotationItem.kind === 'arrow') {
            const rotationHandle = handles.find((handle) => handle.id === 'rotate');
            const connector = rotateMarkerPoint(selectedAnnotationItem, 0, -12);
            ctx.beginPath();
            ctx.moveTo(connector.x, connector.y);
            ctx.lineTo(rotationHandle.x, rotationHandle.y);
            ctx.strokeStyle = '#2563EB';
            ctx.lineWidth = 1.5;
            ctx.stroke();
          }
          handles.forEach((handle) => {
            ctx.beginPath();
            ctx.arc(handle.x, handle.y, MARKER_HANDLE_RADIUS, 0, Math.PI * 2);
            ctx.fillStyle = handle.id === 'rotate' ? '#FFFFFF' : '#2563EB';
            ctx.fill();
            ctx.strokeStyle = '#2563EB';
            ctx.lineWidth = 2;
            ctx.stroke();
          });
        }
        ctx.restore();
      }

      if (includeSelection && selectionRect) {
        const rect = normalizeSelectionRect(selectionRect);
        if (rect.width >= MIN_SELECTION_SIZE && rect.height >= MIN_SELECTION_SIZE) {
          ctx.save();
          ctx.fillStyle = 'rgba(37, 99, 235, 0.10)';
          ctx.strokeStyle = 'rgba(37, 99, 235, 0.95)';
          ctx.lineWidth = 1.5;
          ctx.setLineDash([8, 6]);
          ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
          ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
          ctx.setLineDash([]);
          ctx.fillStyle = '#2563EB';
          ctx.strokeStyle = '#FFFFFF';
          ctx.lineWidth = 1.5;
          getSelectionResizeHandles(rect).forEach((handle) => {
            const bounds = getSelectionResizeHandleBounds(handle);
            ctx.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);
            ctx.strokeRect(bounds.x + 0.5, bounds.y + 0.5, bounds.width - 1, bounds.height - 1);
          });
          ctx.restore();
        }
      }
    },
    [annotationStrokes, ensureImageAsset, graphMap, selectedAnnotationItem, selectedGraphId, selectionRect]
  );

  const renderSelectionImage = useMemo(
    () => async (rect, { format = 'image/jpeg', quality = 0.92 } = {}) => {
      const normalized = normalizeSelectionRect(rect);
      const baseCanvas = canvasRef.current;
      if (!baseCanvas || normalized.width < MIN_SELECTION_SIZE || normalized.height < MIN_SELECTION_SIZE) return null;

      const scaleX = canvasSize.width ? baseCanvas.width / canvasSize.width : 1;
      const scaleY = canvasSize.height ? baseCanvas.height / canvasSize.height : 1;
      const sourceX = normalized.x * scaleX;
      const sourceY = normalized.y * scaleY;
      const sourceWidth = normalized.width * scaleX;
      const sourceHeight = normalized.height * scaleY;

      const exportCanvas = document.createElement('canvas');
      exportCanvas.width = Math.max(1, Math.round(sourceWidth));
      exportCanvas.height = Math.max(1, Math.round(sourceHeight));

      const exportCtx = exportCanvas.getContext('2d');
      exportCtx.fillStyle = canvasBackgroundColor;
      exportCtx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
      exportCtx.drawImage(
        baseCanvas,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        exportCanvas.width,
        exportCanvas.height
      );

      exportCtx.save();
      exportCtx.setTransform(scaleX, 0, 0, scaleY, -sourceX, -sourceY);
      drawAnnotationLayer(exportCtx, { includeSelection: false, includeItemSelection: false });
      exportCtx.restore();

      const blob = await new Promise((resolve) => exportCanvas.toBlob(resolve, format, quality));
      if (!blob) return null;
      const dataUrl = exportCanvas.toDataURL(format, quality);
      return { blob, dataUrl, format };
    },
    [canvasBackgroundColor, canvasSize.height, canvasSize.width, drawAnnotationLayer]
  );

  useEffect(() => {
    const canvas = annotationCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const ratio = window.devicePixelRatio || 1;
    canvas.width = canvasSize.width * ratio;
    canvas.height = canvasSize.height * ratio;
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, canvasSize.width, canvasSize.height);
    drawAnnotationLayer(ctx);
  }, [canvasSize, drawAnnotationLayer]);

  useEffect(() => {
    const canvas = handwritingCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const ratio = window.devicePixelRatio || 1;
    canvas.width = handwritingSize.width * ratio;
    canvas.height = handwritingSize.height * ratio;
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, handwritingSize.width, handwritingSize.height);
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, handwritingSize.width, handwritingSize.height);
    ctx.save();
    ctx.translate(-handwritingOffset.x, -handwritingOffset.y);
    if (showHandwritingGuides) {
      const topLine = Math.floor(handwritingOffset.y / HANDWRITING_LINE_SPACING) * HANDWRITING_LINE_SPACING;
      const bottomLimit = handwritingOffset.y + handwritingSize.height;
      ctx.strokeStyle = '#E5E7EB';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let y = topLine; y <= bottomLimit + HANDWRITING_LINE_SPACING; y += HANDWRITING_LINE_SPACING) {
        ctx.moveTo(handwritingOffset.x, y + 0.5);
        ctx.lineTo(handwritingOffset.x + handwritingSize.width, y + 0.5);
      }
      ctx.stroke();

      ctx.beginPath();
      ctx.strokeStyle = '#F1B5C8';
      ctx.moveTo(HANDWRITING_MARGIN_X, handwritingOffset.y);
      ctx.lineTo(HANDWRITING_MARGIN_X, handwritingOffset.y + handwritingSize.height);
      ctx.stroke();
    }
    handwritingStrokes.forEach((stroke) => {
      if (!stroke) return;
      if (stroke.kind === 'text') {
        ctx.save();
        ctx.fillStyle = '#0F172A';
        ctx.font = `${stroke.size}px sans-serif`;
        ctx.textBaseline = 'top';
        ctx.fillText(stroke.text, stroke.x, stroke.y);
        ctx.restore();
        return;
      }
      if (stroke.kind === 'math') {
        const cached = mathCacheRef.current.get(`${stroke.latex}__${stroke.size}`);
        if (cached?.image?.complete) {
          ctx.drawImage(cached.image, stroke.x, stroke.y, stroke.width, stroke.height);
        }
        return;
      }
      if (!stroke || !Array.isArray(stroke.points) || stroke.points.length === 0) return;
      ctx.beginPath();
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = stroke.tool === 'highlighter' ? 'rgba(250, 204, 21, 0.38)' : '#0F172A';
      ctx.lineWidth = stroke.tool === 'highlighter' ? 14 : 2.8;
      stroke.points.forEach((point, index) => {
        if (index === 0) ctx.moveTo(point.x, point.y);
        else ctx.lineTo(point.x, point.y);
      });
      ctx.stroke();
    });
    ctx.restore();
  }, [handwritingOffset.x, handwritingOffset.y, handwritingSize, handwritingStrokes, showHandwritingGuides]);

  useEffect(() => {
    if (!textDraft) return undefined;
    const frame = window.requestAnimationFrame(() => textInputRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [textDraft]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const ratio = window.devicePixelRatio || 1;
    canvas.width = canvasSize.width * ratio;
    canvas.height = canvasSize.height * ratio;
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, canvasSize.width, canvasSize.height);
    ctx.fillStyle = canvasBackgroundColor;
    ctx.fillRect(0, 0, canvasSize.width, canvasSize.height);

    annotationStrokes.forEach((stroke) => {
      if (!isBehindCanvasImage(stroke)) return;
      const cached = ensureImageAsset(stroke.src);
      if (cached?.image?.complete) {
        ctx.drawImage(cached.image, stroke.x, stroke.y, stroke.width, stroke.height);
      }
    });

    const plot = renderMetrics;
    const xStep = xAxisStep;
    const yStep = axisStep;
    const yAxisX = toCanvas({ x: 0, y: visibleViewport.yMin }, viewport, canvasSize, zoomLevel, axisStep, xAxisStep).x;
    const xAxisY = toCanvas({ x: visibleViewport.xMin, y: 0 }, viewport, canvasSize, zoomLevel, axisStep, xAxisStep).y;
    const axisLabelFont = '20px sans-serif';
    const xLabelY = xAxisY >= plot.top && xAxisY <= plot.bottom ? xAxisY + 12 : plot.bottom + 12;
    const yLabelX = yAxisX >= plot.left && yAxisX <= plot.right ? yAxisX + 12 : plot.left + 12;

    if (gridMode === 'graphPaper') {
      const xMinorStep = xStep / 5;
      const yMinorStep = yStep / 5;

      ctx.beginPath();
      ctx.strokeStyle = '#EDF2F7';
      ctx.lineWidth = 1;
      for (let x = Math.ceil(visibleViewport.xMin / xMinorStep) * xMinorStep; x <= visibleViewport.xMax; x += xMinorStep) {
        const isMajor = Math.abs((x / xStep) - Math.round(x / xStep)) < 1e-6;
        if (isMajor) continue;
        const point = toCanvas({ x, y: 0 }, viewport, canvasSize, zoomLevel, axisStep, xAxisStep);
        ctx.moveTo(point.x, plot.top);
        ctx.lineTo(point.x, plot.bottom);
      }
      for (let y = Math.ceil(visibleViewport.yMin / yMinorStep) * yMinorStep; y <= visibleViewport.yMax; y += yMinorStep) {
        const isMajor = Math.abs((y / yStep) - Math.round(y / yStep)) < 1e-6;
        if (isMajor) continue;
        const point = toCanvas({ x: 0, y }, viewport, canvasSize, zoomLevel, axisStep, xAxisStep);
        ctx.moveTo(plot.left, point.y);
        ctx.lineTo(plot.right, point.y);
      }
      ctx.stroke();
    }

    for (let x = Math.ceil(visibleViewport.xMin / xStep) * xStep; x <= visibleViewport.xMax; x += xStep) {
      const point = toCanvas({ x, y: 0 }, viewport, canvasSize, zoomLevel, axisStep, xAxisStep);
      const isAxis = Math.abs(x) < 1e-9;
      if (!isAxis && gridMode === 'plain') {
        if (showAxisLabels) {
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          ctx.fillStyle = '#64748B';
          ctx.font = axisLabelFont;
          ctx.fillText(formatXAxisLabel(x, xAxisMarker), point.x, xLabelY);
        }
        continue;
      }
      ctx.strokeStyle = isAxis ? '#0F172A' : '#E2E8F0';
      ctx.lineWidth = isAxis ? 2 : 1;
      ctx.beginPath();
      ctx.moveTo(point.x, plot.top);
      ctx.lineTo(point.x, plot.bottom);
      ctx.stroke();
      if (showAxisLabels && !isAxis) {
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillStyle = '#64748B';
        ctx.font = axisLabelFont;
        ctx.fillText(formatXAxisLabel(x, xAxisMarker), point.x, xLabelY);
      }
    }

    for (let y = Math.ceil(visibleViewport.yMin / yStep) * yStep; y <= visibleViewport.yMax; y += yStep) {
      const point = toCanvas({ x: 0, y }, viewport, canvasSize, zoomLevel, axisStep, xAxisStep);
      const isAxis = Math.abs(y) < 1e-9;
      if (!isAxis && gridMode === 'plain') {
        if (showAxisLabels) {
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = '#64748B';
          ctx.font = axisLabelFont;
          ctx.fillText(fmt(y), yLabelX, point.y);
        }
        continue;
      }
      ctx.strokeStyle = isAxis ? '#0F172A' : '#E2E8F0';
      ctx.lineWidth = isAxis ? 2 : 1;
      ctx.beginPath();
      ctx.moveTo(plot.left, point.y);
      ctx.lineTo(plot.right, point.y);
      ctx.stroke();
      if (showAxisLabels && !isAxis) {
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#64748B';
        ctx.font = axisLabelFont;
        ctx.fillText(fmt(y), yLabelX, point.y);
      }
    }

    if (showAxisMarkers) {
      const tickHalf = 6;
      ctx.strokeStyle = '#0F172A';
      ctx.lineWidth = 1.5;

      if (xAxisY >= plot.top && xAxisY <= plot.bottom) {
        for (let x = Math.ceil(visibleViewport.xMin / xStep) * xStep; x <= visibleViewport.xMax; x += xStep) {
          if (Math.abs(x) < 1e-9) continue;
          const point = toCanvas({ x, y: 0 }, viewport, canvasSize, zoomLevel, axisStep, xAxisStep);
          ctx.beginPath();
          ctx.moveTo(point.x, xAxisY - tickHalf);
          ctx.lineTo(point.x, xAxisY + tickHalf);
          ctx.stroke();
        }
      }

      if (yAxisX >= plot.left && yAxisX <= plot.right) {
        for (let y = Math.ceil(visibleViewport.yMin / yStep) * yStep; y <= visibleViewport.yMax; y += yStep) {
          if (Math.abs(y) < 1e-9) continue;
          const point = toCanvas({ x: 0, y }, viewport, canvasSize, zoomLevel, axisStep, xAxisStep);
          ctx.beginPath();
          ctx.moveTo(yAxisX - tickHalf, point.y);
          ctx.lineTo(yAxisX + tickHalf, point.y);
          ctx.stroke();
        }
      }
    }

    const axisArrowSize = 6;
    const axisArrowSpread = 3;
    ctx.strokeStyle = '#1E293B';

    if (xAxisY >= plot.top && xAxisY <= plot.bottom) {
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(plot.right, xAxisY);
      ctx.lineTo(plot.right - axisArrowSize, xAxisY - axisArrowSpread);
      ctx.moveTo(plot.right, xAxisY);
      ctx.lineTo(plot.right - axisArrowSize, xAxisY + axisArrowSpread);
      ctx.stroke();
    }

    if (yAxisX >= plot.left && yAxisX <= plot.right) {
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(yAxisX, plot.top);
      ctx.lineTo(yAxisX - axisArrowSpread, plot.top + axisArrowSize);
      ctx.moveTo(yAxisX, plot.top);
      ctx.lineTo(yAxisX + axisArrowSpread, plot.top + axisArrowSize);
      ctx.stroke();
    }

    if (selectedGraph && analysisMode !== 'inverse') {
      const selected = (x) => evalGraph(selectedGraph, x, graphMap);
      const compared = (x) => evalGraph(compareGraph, x, graphMap);
      let intervals = [];
      if (analysisMode === 'positive') intervals = intervalsFor((x) => (Number.isFinite(selected(x)) ? selected(x) > 0 : null), visibleViewport);
      if (analysisMode === 'negative') intervals = intervalsFor((x) => (Number.isFinite(selected(x)) ? selected(x) < 0 : null), visibleViewport);
      if (analysisMode === 'compare' && compareGraph) intervals = intervalsFor((x) => (Number.isFinite(selected(x)) && Number.isFinite(compared(x)) ? selected(x) > compared(x) : null), visibleViewport);
      if (analysisMode === 'increasing') intervals = intervalsFor((x) => {
        const dx = (visibleViewport.xMax - visibleViewport.xMin) / SAMPLES;
        const first = selected(x);
        const second = selected(x + dx);
        return Number.isFinite(first) && Number.isFinite(second) ? second - first > 0.001 : null;
      }, visibleViewport);
      if (analysisMode === 'decreasing') intervals = intervalsFor((x) => {
        const dx = (visibleViewport.xMax - visibleViewport.xMin) / SAMPLES;
        const first = selected(x);
        const second = selected(x + dx);
        return Number.isFinite(first) && Number.isFinite(second) ? second - first < -0.001 : null;
      }, visibleViewport);
      if (analysisMode === 'productPositive' && compareGraph) intervals = intervalsFor((x) => (Number.isFinite(selected(x)) && Number.isFinite(compared(x)) ? selected(x) * compared(x) > 0 : null), visibleViewport);
      if (analysisMode === 'quotientPositive' && compareGraph) intervals = intervalsFor((x) => {
        const first = selected(x);
        const second = compared(x);
        return Number.isFinite(first) && Number.isFinite(second) && Math.abs(second) > 1e-6 ? first / second > 0 : null;
      }, visibleViewport);
      ctx.fillStyle = 'rgba(37, 99, 235, 0.14)';
      intervals.forEach(([start, end]) => {
        const startPoint = toCanvas({ x: start, y: visibleViewport.yMin }, viewport, canvasSize, zoomLevel, axisStep, xAxisStep).x;
        const endPoint = toCanvas({ x: end, y: visibleViewport.yMax }, viewport, canvasSize, zoomLevel, axisStep, xAxisStep).x;
        ctx.fillRect(startPoint, plot.top, endPoint - startPoint, plot.height);
      });
    }

    if (analysisMode === 'inverse' && selectedGraph) {
      const start = toCanvas({ x: visibleViewport.xMin, y: visibleViewport.xMin }, viewport, canvasSize, zoomLevel, axisStep, xAxisStep);
      const end = toCanvas({ x: visibleViewport.xMax, y: visibleViewport.xMax }, viewport, canvasSize, zoomLevel, axisStep, xAxisStep);
      ctx.setLineDash([8, 6]);
      ctx.strokeStyle = '#94A3B8';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    graphs.forEach((graph) => {
      if (graph.visible === false) return;
      const graphViewport = graphUsesTrigEquation(graph, graphMap)
        ? constrainViewportToXDomain(visibleViewport, viewport)
        : visibleViewport;
      if (!graphViewport) return;
      const segments = sampleSegments(graph, graphViewport, graphMap);
      ctx.strokeStyle = graph.color;
      ctx.fillStyle = graph.color;
      ctx.lineWidth = graph.id === selectedGraphId ? 3 : 2;
      ctx.setLineDash(getGraphLineDash(graph.lineStyle));
      segments.forEach((segment) => {
        ctx.beginPath();
        segment.forEach((point, index) => {
          const canvasPoint = toCanvas(point, viewport, canvasSize, zoomLevel, axisStep, xAxisStep);
          if (index === 0) ctx.moveTo(canvasPoint.x, canvasPoint.y);
          else ctx.lineTo(canvasPoint.x, canvasPoint.y);
        });
        ctx.stroke();
      });
      if (graph.kind === 'table') {
        graph.points.forEach((point) => {
          const canvasPoint = toCanvas(point, viewport, canvasSize, zoomLevel, axisStep, xAxisStep);
          ctx.beginPath();
          ctx.arc(canvasPoint.x, canvasPoint.y, 4, 0, Math.PI * 2);
          ctx.fill();
        });
      }
    });
  }, [analysisMode, annotationStrokes, axisStep, canvasBackgroundColor, canvasSize, compareGraph, ensureImageAsset, graphMap, graphs, gridMode, renderMetrics, selectedGraph, selectedGraphId, showAxisLabels, showAxisMarkers, viewport, visibleViewport, xAxisMarker, xAxisStep, zoomLevel]);

  const changeEquationGroup = (group) => {
    const nextGroup = normalizeEquationGroup(group);
    const nextType = equationTypeForGroup(nextGroup, equationType);
    setEquationGroup(nextGroup);
    setViewport((current) => viewportForEquationGroup(nextGroup, current));
    if (nextGroup === 'trig') {
      setXAxisMarker('degree30');
    }
    if (nextType !== equationType) {
      setEquationType(nextType);
      setEquationParams(makeParams(nextType));
    }
    setEquationDropdownOpen(false);
  };

  const addEquationGraph = () => {
    const params = Object.fromEntries(Object.entries(equationParams).map(([key, value]) => [key, num(value)]));
    const graph = {
      id: graphId(),
      kind: 'function',
      equationType,
      params,
      visible: true,
      color: nextColor(graphs),
      lineStyle: 'solid',
      title: equationTitle.trim() || EQUATIONS[equationType].label,
    };
    setGraphs((current) => [...current, graph]);
    setSelectedGraphId(graph.id);
    if (!compareGraphId) setCompareGraphId(graph.id);
    setEquationTitle('');
  };

  const addTableGraph = () => {
    const points = tableRows.map((row) => ({ x: Number(row.x), y: Number(row.y) })).filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));
    if (points.length < 2) return;
    const graph = { id: graphId(), kind: 'table', points, visible: true, color: nextColor(graphs), lineStyle: 'solid', title: tableTitle.trim() || 'Table Plot' };
    setGraphs((current) => [...current, graph]);
    setSelectedGraphId(graph.id);
    if (!compareGraphId) setCompareGraphId(graph.id);
  };

  const addTransformGraph = () => {
    if (!selectedGraph) return;
    const horizontalShift = num(transform.horizontalShift);
    const verticalShift = num(transform.verticalShift);

    if (transform.mode === 'apply') {
      if (selectedGraph.kind === 'function') {
        const nextParams = { ...selectedGraph.params };
        if ('c' in nextParams && selectedGraph.equationType === 'verticalLine') {
          nextParams.c -= horizontalShift;
        }
        if ('p' in nextParams) {
          nextParams.p += isTrigEquation(selectedGraph.equationType) ? horizontalShift : -horizontalShift;
        }
        if ('q' in nextParams) nextParams.q += verticalShift;
        if ('b' in nextParams && selectedGraph.equationType === 'linear') {
          nextParams.b += verticalShift + (nextParams.m || 0) * horizontalShift;
        }
        if ('d' in nextParams && selectedGraph.equationType === 'cubicStandard') {
          const a = nextParams.a || 0;
          const b = nextParams.b || 0;
          const c = nextParams.c || 0;
          const d = nextParams.d || 0;
          nextParams.b = b + 3 * a * horizontalShift;
          nextParams.c = c + 2 * b * horizontalShift + 3 * a * horizontalShift * horizontalShift;
          nextParams.d = d + c * horizontalShift + b * horizontalShift * horizontalShift + a * horizontalShift * horizontalShift * horizontalShift + verticalShift;
        }
        if ('c' in nextParams && selectedGraph.equationType === 'quadraticStandard') {
          const a = nextParams.a || 0;
          const b = nextParams.b || 0;
          const c = nextParams.c || 0;
          nextParams.b = b + 2 * a * horizontalShift;
          nextParams.c = c + a * horizontalShift * horizontalShift + b * horizontalShift + verticalShift;
        }
        if (transform.reflectX) {
          if ('A' in nextParams) nextParams.A *= -1;
          if ('a' in nextParams) nextParams.a *= -1;
          if ('k' in nextParams && selectedGraph.equationType === 'hyperbola') nextParams.k *= -1;
          if ('m' in nextParams) nextParams.m *= -1;
          if ('b' in nextParams && selectedGraph.equationType === 'linear') nextParams.b *= -1;
          if ('b' in nextParams && selectedGraph.equationType === 'quadraticStandard') nextParams.b *= -1;
          if ('b' in nextParams && selectedGraph.equationType === 'cubicStandard') nextParams.b *= -1;
          if ('c' in nextParams && selectedGraph.equationType === 'quadraticStandard') nextParams.c *= -1;
          if ('c' in nextParams && selectedGraph.equationType === 'cubicStandard') nextParams.c *= -1;
          if ('d' in nextParams && selectedGraph.equationType === 'cubicStandard') nextParams.d *= -1;
          if ('q' in nextParams) nextParams.q *= -1;
        }
        if (transform.reflectY) {
          if ('c' in nextParams && selectedGraph.equationType === 'verticalLine') nextParams.c *= -1;
          if ('p' in nextParams) nextParams.p *= -1;
          if ('m' in nextParams) nextParams.m *= -1;
          if ('b' in nextParams && selectedGraph.equationType === 'quadraticStandard') nextParams.b *= -1;
          if ('a' in nextParams && selectedGraph.equationType === 'cubicStandard') nextParams.a *= -1;
          if ('c' in nextParams && selectedGraph.equationType === 'cubicStandard') nextParams.c *= -1;
          if ('k' in nextParams && ['sine', 'cosine', 'tangent'].includes(selectedGraph.equationType)) nextParams.k *= -1;
        }

        setGraphs((current) =>
          current.map((graph) =>
            graph.id === selectedGraph.id
              ? {
                  ...graph,
                  params: nextParams,
                }
              : graph
          )
        );
        return;
      }

      if (selectedGraph.kind === 'table') {
        const nextPoints = selectedGraph.points.map((point) => ({
          x: (transform.reflectY ? -point.x : point.x) - horizontalShift,
          y: (transform.reflectX ? -point.y : point.y) + verticalShift,
        }));
        setGraphs((current) =>
          current.map((graph) =>
            graph.id === selectedGraph.id
              ? {
                  ...graph,
                  points: nextPoints,
                }
              : graph
          )
        );
        return;
      }
    }

    const graph = {
      id: graphId(),
      kind: 'transform',
      sourceGraphId: selectedGraph.id,
      visible: true,
      horizontalShift,
      verticalShift,
      reflectX: transform.reflectX,
      reflectY: transform.reflectY,
      color: nextColor(graphs),
      lineStyle: selectedGraph.lineStyle || 'solid',
      title: `${selectedGraph.title} transform`,
    };
    setGraphs((current) => [...current, graph]);
    setSelectedGraphId(graph.id);
  };

  const addDerivativeGraph = () => {
    if (!selectedGraph) return;
    const graph = {
      id: graphId(),
      kind: 'derivative',
      sourceGraphId: selectedGraph.id,
      visible: true,
      color: nextColor(graphs),
      lineStyle: 'solid',
      title: `${selectedGraph.title} derivative`,
    };
    setGraphs((current) => [...current, graph]);
    setSelectedGraphId(graph.id);
  };

  const addInverseGraph = () => {
    if (!selectedGraph) return;
    const graph = {
      id: graphId(),
      kind: 'inverse',
      sourceGraphId: selectedGraph.id,
      visible: true,
      color: nextColor(graphs),
      lineStyle: 'dashed',
      title: `${selectedGraph.title} inverse`,
    };
    setGraphs((current) => [...current, graph]);
    setSelectedGraphId(graph.id);
    setAnalysisMode('inverse');
  };

  const removeGraph = (id) => {
    setGraphs((current) => current.filter((graph) => graph.id !== id));
    if (selectedGraphId === id) setSelectedGraphId('');
    if (compareGraphId === id) setCompareGraphId('');
  };

  const toggleGraphVisibility = (id) => {
    setGraphs((current) =>
      current.map((graph) =>
        graph.id === id
          ? {
              ...graph,
              visible: !(graph.visible ?? true),
            }
          : graph
      )
    );
  };

  const updateSelectedGraphLineStyle = (lineStyle) => {
    if (!selectedGraph) return;
    setGraphs((current) =>
      current.map((graph) =>
        graph.id === selectedGraph.id
          ? {
              ...graph,
              lineStyle,
            }
          : graph
      )
    );
  };

  const updateSelectedGraphColor = (color) => {
    if (!selectedGraph) return;
    setGraphs((current) =>
      current.map((graph) =>
        graph.id === selectedGraph.id
          ? {
              ...graph,
              color,
            }
          : graph
      )
    );
  };

  const updateCanvasBackgroundColor = (color) => {
    const normalized = normalizeCanvasBackgroundColor(color);
    setCanvasBackgroundColor(normalized);
    setCanvasBackgroundInput(normalized);
  };

  const handleCanvasBackgroundInputChange = (value) => {
    setCanvasBackgroundInput(value);
    if (isValidCanvasBackgroundColor(value)) {
      setCanvasBackgroundColor(normalizeCanvasBackgroundColor(value));
    }
  };

  const handleCanvasBackgroundInputBlur = () => {
    setCanvasBackgroundInput((current) =>
      isValidCanvasBackgroundColor(current)
        ? normalizeCanvasBackgroundColor(current)
        : canvasBackgroundColor
    );
  };

  const getAnnotationPoint = (event) => {
    const canvas = annotationCanvasRef.current;
    const rect = canvas?.getBoundingClientRect();
    if (!canvas || !rect) return null;
    const scaleX = rect.width ? canvasSize.width / rect.width : 1;
    const scaleY = rect.height ? canvasSize.height / rect.height : 1;
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    };
  };

  const findGraphAtCanvasPoint = (point) => {
    const hitRadius = 9;
    let closestGraph = null;
    let closestDistance = hitRadius;
    for (let graphIndex = graphs.length - 1; graphIndex >= 0; graphIndex -= 1) {
      const graph = graphs[graphIndex];
      if (!graph || graph.visible === false) continue;
      const graphViewport = graphUsesTrigEquation(graph, graphMap)
        ? constrainViewportToXDomain(visibleViewport, viewport)
        : visibleViewport;
      if (!graphViewport) continue;
      const segments = sampleSegments(graph, graphViewport, graphMap);
      for (const segment of segments) {
        for (let index = 1; index < segment.length; index += 1) {
          const start = toCanvas(segment[index - 1], viewport, canvasSize, zoomLevel, axisStep, xAxisStep);
          const end = toCanvas(segment[index], viewport, canvasSize, zoomLevel, axisStep, xAxisStep);
          const distance = distanceToSegment(point, start, end);
          if (distance <= closestDistance) {
            closestDistance = distance;
            closestGraph = graph;
          }
        }
      }
      if (graph.kind === 'table') {
        graph.points.forEach((tablePoint) => {
          const canvasPoint = toCanvas(tablePoint, viewport, canvasSize, zoomLevel, axisStep, xAxisStep);
          const distance = Math.hypot(point.x - canvasPoint.x, point.y - canvasPoint.y);
          if (distance <= closestDistance) {
            closestDistance = distance;
            closestGraph = graph;
          }
        });
      }
    }
    return closestGraph;
  };

  const getHandwritingPoint = (event) => {
    const canvas = handwritingCanvasRef.current;
    const rect = canvas?.getBoundingClientRect();
    if (!canvas || !rect) return null;
    const scaleX = rect.width ? handwritingSize.width / rect.width : 1;
    const scaleY = rect.height ? handwritingSize.height / rect.height : 1;
    const canvasX = (event.clientX - rect.left) * scaleX;
    const canvasY = (event.clientY - rect.top) * scaleY;
    return {
      x: canvasX + handwritingOffset.x,
      y: canvasY + handwritingOffset.y,
      canvasX,
      canvasY,
    };
  };

  const getAnnotationItemBounds = (item) => {
    if (item.kind === 'text') return getTextBounds(item);
    if (item.kind === 'math') {
      return {
        x: item.x,
        y: item.y,
        width: item.width,
        height: item.height,
      };
    }
    if (item.kind === 'image') return getImageBounds(item);
    if (['arrow', 'dot'].includes(item.kind)) return getMarkerBounds(item);
    return null;
  };

  const findItemAtPoint = (items, point) => {
    const passes = [
      (item) => !isBehindCanvasImage(item),
      (item) => isBehindCanvasImage(item),
    ];
    for (const matchesPass of passes) {
      for (let index = items.length - 1; index >= 0; index -= 1) {
        const item = items[index];
        if (!item || !['text', 'math', 'image', 'arrow', 'dot'].includes(item.kind) || !matchesPass(item)) continue;
        const bounds = getAnnotationItemBounds(item);
        if (bounds && pointInBounds(point, bounds)) return item;
      }
    }
    return null;
  };

  const isPointInImageResizeHandle = (item, point) => {
    if (item?.kind !== 'image') return false;
    return pointInBounds(point, getImageResizeHandleBounds(getImageBounds(item)));
  };

  const getMarkerHandleAtPoint = (item, point) => {
    if (!item || !['arrow', 'dot'].includes(item.kind)) return null;
    return getMarkerHandles(item).find((handle) =>
      Math.hypot(point.x - handle.x, point.y - handle.y) <= MARKER_HANDLE_RADIUS + 4
    ) || null;
  };

  const getSelectionResizeHandleAtPoint = (point) => {
    if (!selectionRect) return null;
    const rect = normalizeSelectionRect(selectionRect);
    if (rect.width < MIN_SELECTION_SIZE || rect.height < MIN_SELECTION_SIZE) return null;
    return getSelectionResizeHandles(rect).find((handle) =>
      pointInBounds(point, getSelectionResizeHandleBounds(handle))
    ) || null;
  };

  const beginTextDraft = (surface, point) => {
    setTextDraft({
      surface,
      text: '',
      x: point.x,
      y: point.y,
      size: annotationTextSize,
    });
  };

  const addGraphMarker = useCallback((kind) => {
    const marker = {
      id: `${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      kind,
      x: canvasSize.width / 2,
      y: canvasSize.height / 2,
      color: selectedGraph?.color || '#1E293B',
      graphId: selectedGraph?.id || '',
      lineStyle: selectedGraph?.lineStyle || 'solid',
      ...(kind === 'arrow' ? { length: 34, rotation: 0, lineWidth: selectedGraph ? 3 : 2 } : { radius: 7 }),
    };
    setAnnotationFuture([]);
    setAnnotationStrokes((current) => [...current, marker]);
    setSelectedAnnotationItemId(marker.id);
    setSelectionRect(null);
    setSelectionMenu(null);
    setAnnotationTool('pointer');
    setActiveSurface('graph');
    canvasWrapRef.current?.focus?.();
  }, [canvasSize.height, canvasSize.width, selectedGraph]);

  const updateSelectedMarker = useCallback((updates) => {
    if (!selectedAnnotationItemId) return;
    setAnnotationFuture([]);
    setAnnotationStrokes((current) => current.map((item) =>
      item?.id === selectedAnnotationItemId && ['arrow', 'dot'].includes(item.kind)
        ? { ...item, ...updates }
        : item
    ));
  }, [selectedAnnotationItemId]);

  const beginMathDraft = (surface, point, anchorRect, initialLatex = '', editingId = null) => {
    setMathDraft({
      surface,
      point,
      anchorRect,
      initialLatex,
      size: annotationTextSize,
      editingId,
    });
  };

  const commitTextDraft = () => {
    if (!textDraft) return;
    const value = textDraft.text.trim();
    if (!value) {
      setTextDraft(null);
      return;
    }
    const textItem = {
      id: `text-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      kind: 'text',
      text: value,
      x: textDraft.x,
      y: textDraft.y,
      size: textDraft.size,
    };
    if (textDraft.surface === 'handwriting') {
      setHandwritingFuture([]);
      setHandwritingStrokes((current) => [...current, textItem]);
    } else {
      setAnnotationFuture([]);
      setAnnotationStrokes((current) => [...current, textItem]);
    }
    setTextDraft(null);
  };

  const commitMathDraft = useCallback(async (latex) => {
    const trimmed = latex?.trim();
    if (!mathDraft || !trimmed) {
      setMathDraft(null);
      return;
    }

    const asset = await buildMathAsset(trimmed, mathDraft.size);
    const mathItem = {
      id: mathDraft.editingId || `math-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      kind: 'math',
      latex: trimmed,
      x: mathDraft.point.x,
      y: mathDraft.point.y,
      width: asset.width,
      height: asset.height,
      size: mathDraft.size,
    };

    if (mathDraft.surface === 'handwriting') {
      setHandwritingFuture([]);
      setHandwritingStrokes((current) =>
        mathDraft.editingId
          ? current.map((item) => (item?.id === mathDraft.editingId ? mathItem : item))
          : [...current, mathItem]
      );
    } else {
      setAnnotationFuture([]);
      setAnnotationStrokes((current) =>
        mathDraft.editingId
          ? current.map((item) => (item?.id === mathDraft.editingId ? mathItem : item))
          : [...current, mathItem]
      );
    }
    setMathDraft(null);
  }, [buildMathAsset, mathDraft]);

  const handleAnnotationPointerDown = (event) => {
    canvasWrapRef.current?.focus?.();
    setActiveSurface('graph');
    const point = getAnnotationPoint(event);
    if (!point) return;
    if (annotationTool === 'pan') {
      annotationRef.current.drawing = true;
      annotationRef.current.currentStroke = {
        kind: 'viewport-drag',
        startPoint: point,
        startViewport: viewport,
        startAnnotationStrokes: cloneValue(annotationStrokes),
      };
      setSelectionMenu(null);
      setSelectionRect(null);
      return;
    }
    if (annotationTool === 'text') {
      if (annotationTextMode === 'math') {
        beginMathDraft('graph', point, {
          left: event.clientX,
          top: event.clientY,
        });
      } else {
        beginTextDraft('graph', point);
      }
      setSelectionMenu(null);
      setSelectionRect(null);
      return;
    }
    if (annotationTool === 'pointer') {
      const selectionHandle = getSelectionResizeHandleAtPoint(point);
      if (selectionHandle && selectionRect) {
        const rect = normalizeSelectionRect(selectionRect);
        annotationRef.current.drawing = true;
        annotationRef.current.currentStroke = {
          kind: 'selection-resize',
          handleId: selectionHandle.id,
          startRect: rect,
          anchor: getSelectionResizeAnchor(rect, selectionHandle.id),
        };
        setSelectedAnnotationItemId('');
        setSelectionRect(rect);
        setSelectionMenu(null);
        setSelectionResizeCursor(selectionHandle.cursor);
        return;
      }
      const selectedMarkerHandle = getMarkerHandleAtPoint(selectedAnnotationItem, point);
      if (selectedMarkerHandle) {
        annotationRef.current.drawing = true;
        annotationRef.current.currentStroke = {
          kind: selectedMarkerHandle.id === 'rotate' ? 'marker-rotate' : 'marker-resize',
          id: selectedAnnotationItem.id,
          markerKind: selectedAnnotationItem.kind,
          center: { x: selectedAnnotationItem.x, y: selectedAnnotationItem.y },
        };
        setSelectionMenu(null);
        setSelectionRect(null);
        return;
      }
      const targetItem = findItemAtPoint(annotationStrokes, point);
      if (targetItem) {
        if (isPointInImageResizeHandle(targetItem, point)) {
          annotationRef.current.drawing = true;
          annotationRef.current.currentStroke = {
            kind: 'image-resize',
            id: targetItem.id,
            startPoint: point,
            startWidth: targetItem.width,
            startHeight: targetItem.height,
            startX: targetItem.x,
            startY: targetItem.y,
            aspectRatio: targetItem.aspectRatio || (targetItem.width && targetItem.height ? targetItem.width / targetItem.height : 1),
          };
          setSelectedAnnotationItemId(targetItem.id);
          setSelectionMenu(null);
          setSelectionRect(null);
          return;
        }
        annotationRef.current.drawing = true;
        annotationRef.current.currentStroke = {
          kind: 'annotation-drag',
          id: targetItem.id,
          offsetX: point.x - targetItem.x,
          offsetY: point.y - targetItem.y,
        };
        setSelectedAnnotationItemId(targetItem.id);
        setSelectionMenu(null);
        setSelectionRect(null);
        return;
      }
      const targetGraph = findGraphAtCanvasPoint(point);
      if (targetGraph) {
        setSelectedGraphId(targetGraph.id);
        setSelectedAnnotationItemId('');
        setSelectionRect(null);
        setSelectionMenu(null);
        setSelectionResizeCursor('');
        annotationRef.current.drawing = false;
        annotationRef.current.currentStroke = null;
        return;
      }
      annotationRef.current.drawing = true;
      annotationRef.current.currentStroke = {
        kind: 'selection-drag',
        startPoint: point,
        endPoint: point,
      };
      setSelectedAnnotationItemId('');
      setSelectionRect({ x: point.x, y: point.y, width: 0, height: 0 });
      setSelectionMenu(null);
      return;
    }
    if (annotationTool === 'eraser') {
      const targetItem = findItemAtPoint(annotationStrokes, point);
      if (targetItem) {
        if (selectedAnnotationItemId === targetItem.id) setSelectedAnnotationItemId('');
        setAnnotationStrokes((current) => current.filter((item) => item?.id !== targetItem.id));
        setAnnotationFuture((future) => [...future, targetItem]);
        return;
      }
      setAnnotationStrokes((current) => {
        if (!current.length) return current;
        const targetStrokeId = findStrokeAtPoint(current, point);
        if (!targetStrokeId) return current;
        const removedStroke = current.find((stroke) => stroke?.id === targetStrokeId);
        if (removedStroke) {
          setAnnotationFuture((future) => [...future, removedStroke]);
        }
        return current.filter((stroke) => stroke?.id !== targetStrokeId);
      });
      return;
    }
    setSelectedAnnotationItemId('');
    annotationRef.current.drawing = true;
    annotationRef.current.currentStroke = {
      id: `stroke-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      tool: annotationTool,
      points: [point],
    };
    setAnnotationFuture([]);
    setAnnotationStrokes((current) => [...current, annotationRef.current.currentStroke]);
  };

  const handleAnnotationPointerMove = (event) => {
    const point = getAnnotationPoint(event);
    if (!point) return;
    if (annotationTool === 'pan') {
      if (!annotationRef.current.drawing || !annotationRef.current.currentStroke) return;
      if (annotationRef.current.currentStroke.kind === 'viewport-drag') {
        const deltaX = (annotationRef.current.currentStroke.startPoint.x - point.x) / renderMetrics.xScale;
        const deltaY = (point.y - annotationRef.current.currentStroke.startPoint.y) / renderMetrics.yScale;
        const canvasDeltaX = point.x - annotationRef.current.currentStroke.startPoint.x;
        const canvasDeltaY = point.y - annotationRef.current.currentStroke.startPoint.y;
        setViewport({
          ...annotationRef.current.currentStroke.startViewport,
          xMin: annotationRef.current.currentStroke.startViewport.xMin + deltaX,
          xMax: annotationRef.current.currentStroke.startViewport.xMax + deltaX,
          yMin: annotationRef.current.currentStroke.startViewport.yMin + deltaY,
          yMax: annotationRef.current.currentStroke.startViewport.yMax + deltaY,
        });
        setAnnotationStrokes(
          annotationRef.current.currentStroke.startAnnotationStrokes.map((item) =>
            translateAnnotationItem(item, canvasDeltaX, canvasDeltaY)
          )
        );
      }
      return;
    }
    if (annotationTool === 'pointer') {
      if (!annotationRef.current.drawing || !annotationRef.current.currentStroke) {
        const selectionHandle = getSelectionResizeHandleAtPoint(point);
        setSelectionResizeCursor((current) => (current === (selectionHandle?.cursor || '') ? current : selectionHandle?.cursor || ''));
        return;
      }
      if (annotationRef.current.currentStroke.kind === 'selection-resize') {
        const { startRect, handleId, anchor } = annotationRef.current.currentStroke;
        const nextRect = resizeSelectionRect(startRect, handleId, anchor, point);
        annotationRef.current.currentStroke = {
          ...annotationRef.current.currentStroke,
          currentRect: nextRect,
        };
        setSelectionRect(nextRect);
        return;
      }
      if (annotationRef.current.currentStroke.kind === 'image-resize') {
        const { id, startPoint, startWidth, startHeight, aspectRatio } = annotationRef.current.currentStroke;
        const deltaX = point.x - startPoint.x;
        const deltaY = point.y - startPoint.y;
        const widthRatio = startWidth > 0 ? (startWidth + deltaX) / startWidth : 1;
        const heightRatio = startHeight > 0 ? (startHeight + deltaY) / startHeight : 1;
        const scale = Math.max(MIN_IMAGE_SIZE / Math.max(1, startWidth), widthRatio, heightRatio);
        const nextWidth = Math.max(MIN_IMAGE_SIZE, Math.round(startWidth * scale));
        const nextHeight = Math.max(MIN_IMAGE_SIZE, Math.round(nextWidth / Math.max(0.01, aspectRatio || 1)));
        setAnnotationStrokes((current) =>
          current.map((item) =>
            item?.id === id && item.kind === 'image'
              ? { ...item, width: nextWidth, height: nextHeight }
              : item
          )
        );
        return;
      }
      if (annotationRef.current.currentStroke.kind === 'marker-rotate') {
        const { id, center } = annotationRef.current.currentStroke;
        const rotation = (Math.atan2(point.y - center.y, point.x - center.x) * 180) / Math.PI + 90;
        setAnnotationStrokes((current) => current.map((item) =>
          item?.id === id && item.kind === 'arrow' ? { ...item, rotation } : item
        ));
        return;
      }
      if (annotationRef.current.currentStroke.kind === 'marker-resize') {
        const { id, markerKind, center } = annotationRef.current.currentStroke;
        const distance = Math.hypot(point.x - center.x, point.y - center.y);
        setAnnotationStrokes((current) => current.map((item) => {
          if (item?.id !== id) return item;
          if (markerKind === 'dot' && item.kind === 'dot') {
            return { ...item, radius: clamp(distance / Math.SQRT2, MIN_DOT_RADIUS, MAX_DOT_RADIUS) };
          }
          if (markerKind === 'arrow' && item.kind === 'arrow') {
            return {
              ...item,
              length: distance < 6 ? 0 : clamp(distance * 2, MIN_ARROW_LENGTH, MAX_ARROW_LENGTH),
            };
          }
          return item;
        }));
        return;
      }
      if (annotationRef.current.currentStroke.kind === 'annotation-drag') {
        const { id, offsetX, offsetY } = annotationRef.current.currentStroke;
        setAnnotationStrokes((current) =>
          current.map((item) =>
            item?.id === id && ['text', 'math', 'image', 'arrow', 'dot'].includes(item.kind)
              ? {
                ...item,
                x: point.x - offsetX,
                y: point.y - offsetY,
              }
              : item
          )
        );
        return;
      }
      const { startPoint } = annotationRef.current.currentStroke;
      const nextRect = {
        x: startPoint.x,
        y: startPoint.y,
        width: point.x - startPoint.x,
        height: point.y - startPoint.y,
      };
      annotationRef.current.currentStroke = {
        ...annotationRef.current.currentStroke,
        endPoint: point,
      };
      setSelectionRect(nextRect);
      return;
    }
    if (!annotationRef.current.drawing || !annotationRef.current.currentStroke) return;
    const nextStroke = {
      ...annotationRef.current.currentStroke,
      points: [...annotationRef.current.currentStroke.points, point],
    };
    annotationRef.current.currentStroke = nextStroke;
    setAnnotationStrokes((current) =>
      current.map((stroke) =>
        stroke && stroke.id === nextStroke.id ? nextStroke : stroke
      )
    );
  };

  const endAnnotationStroke = () => {
    if (annotationTool === 'pan') {
      annotationRef.current.drawing = false;
      annotationRef.current.currentStroke = null;
      return;
    }
    if (annotationTool === 'pointer') {
      const currentPointerStroke = annotationRef.current.currentStroke;
      if (['annotation-drag', 'image-resize', 'marker-resize', 'marker-rotate'].includes(currentPointerStroke?.kind)) {
        if (currentPointerStroke?.id) {
          setSelectedAnnotationItemId(currentPointerStroke.id);
        }
        setSelectionRect(null);
        setSelectionMenu(null);
        annotationRef.current.drawing = false;
        annotationRef.current.currentStroke = null;
        return;
      }
      const rect = currentPointerStroke?.kind === 'selection-resize'
        ? normalizeSelectionRect(currentPointerStroke.currentRect || selectionRect || currentPointerStroke.startRect)
        : currentPointerStroke?.startPoint && currentPointerStroke?.endPoint
        ? normalizeSelectionRect({
          x: currentPointerStroke.startPoint.x,
          y: currentPointerStroke.startPoint.y,
          width: currentPointerStroke.endPoint.x - currentPointerStroke.startPoint.x,
          height: currentPointerStroke.endPoint.y - currentPointerStroke.startPoint.y,
        })
        : selectionRect
          ? normalizeSelectionRect(selectionRect)
          : null;
      if (rect && rect.width >= MIN_SELECTION_SIZE && rect.height >= MIN_SELECTION_SIZE) {
        setSelectionRect(rect);
        setSelectionMenu({
          left: Math.min(canvasSize.width - 188, rect.x + rect.width + 12),
          top: Math.max(12, rect.y + 8),
        });
      } else {
        setSelectionRect(null);
        setSelectionMenu(null);
      }
    }
    annotationRef.current.drawing = false;
    annotationRef.current.currentStroke = null;
  };

  const clearToolCursor = () => {
    endAnnotationStroke();
  };

  const clearSelection = () => {
    setSelectionRect(null);
    setSelectionMenu(null);
    setSelectionResizeCursor('');
  };

  const removeSelectedAnnotationItem = useCallback(() => {
    if (!selectedAnnotationItemId || !selectedAnnotationItem) return false;
    setAnnotationStrokes((current) => current.filter((item) => item?.id !== selectedAnnotationItemId));
    setAnnotationFuture((future) => [...future, selectedAnnotationItem]);
    setSelectedAnnotationItemId('');
    setSelectionRect(null);
    setSelectionMenu(null);
    setSelectionResizeCursor('');
    return true;
  }, [selectedAnnotationItem, selectedAnnotationItemId]);

  const handleCanvasKeyDown = useCallback((event) => {
    if (!['Delete', 'Backspace'].includes(event.key)) return;
    if (annotationTool !== 'pointer' || activeSurface !== 'graph') return;
    if (event.target instanceof HTMLElement && event.target.closest('input, textarea, select, button, [contenteditable="true"]')) {
      return;
    }
    if (removeSelectedAnnotationItem()) {
      event.preventDefault();
    }
  }, [activeSurface, annotationTool, removeSelectedAnnotationItem]);

  const handleAnnotationUndo = () => {
    setAnnotationStrokes((current) => {
      if (!current.length) return current;
      const lastStroke = current[current.length - 1];
      setAnnotationFuture((future) => [...future, lastStroke]);
      return current.slice(0, -1);
    });
  };

  const handleAnnotationRedo = () => {
    setAnnotationFuture((future) => {
      if (!future.length) return future;
      const restoredStroke = future[future.length - 1];
      setAnnotationStrokes((current) => [...current, restoredStroke]);
      return future.slice(0, -1);
    });
  };

  const handleAnnotationDoubleClick = (event) => {
    if (annotationTool !== 'pointer') return;
    canvasWrapRef.current?.focus?.();
    const point = getAnnotationPoint(event);
    if (!point) return;
    const targetItem = findItemAtPoint(annotationStrokes, point);
    if (targetItem?.kind !== 'math') return;
    annotationRef.current.drawing = false;
    annotationRef.current.currentStroke = null;
    beginMathDraft(
      'graph',
      { x: targetItem.x, y: targetItem.y },
      { left: event.clientX, top: event.clientY },
      targetItem.latex || '',
      targetItem.id
    );
    setSelectionMenu(null);
    setSelectionRect(null);
  };

  const handleCanvasPaste = useCallback(async (event) => {
    if (event.target instanceof HTMLElement && (event.target.closest('input, textarea, [contenteditable="true"]'))) {
      return;
    }
    const items = Array.from(event.clipboardData?.items || []);
    const imageFiles = items
      .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
      .map((item) => item.getAsFile())
      .filter(Boolean);
    if (!imageFiles.length) return;
    event.preventDefault();
    canvasWrapRef.current?.focus?.();
    for (const file of imageFiles) {
      try {
        await addImageToCanvas(file);
      } catch (error) {
        console.error('Failed to paste image into graph canvas:', error);
      }
    }
  }, [addImageToCanvas]);

  const handleCanvasDragOver = useCallback((event) => {
    const hasImageFile = Array.from(event.dataTransfer?.items || []).some((item) => item.kind === 'file' && item.type.startsWith('image/'));
    if (!hasImageFile) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleCanvasDrop = useCallback(async (event) => {
    const files = Array.from(event.dataTransfer?.files || []).filter((file) => file.type.startsWith('image/'));
    if (!files.length) return;
    event.preventDefault();
    canvasWrapRef.current?.focus?.();
    const point = getAnnotationPoint(event);
    let offsetIndex = 0;
    for (const file of files) {
      try {
        const targetPoint = point
          ? { x: point.x + offsetIndex * 24, y: point.y + offsetIndex * 24 }
          : null;
        await addImageToCanvas(file, targetPoint);
        offsetIndex += 1;
      } catch (error) {
        console.error('Failed to drop image into graph canvas:', error);
      }
    }
  }, [addImageToCanvas]);

  const handleCopySelection = async () => {
    if (!selectionRect || !navigator.clipboard?.write || typeof window.ClipboardItem === 'undefined') return;
    try {
      const image = await renderSelectionImage(selectionRect, { format: 'image/png' });
      if (!image) {
        setSelectionCopyFeedback({ type: 'error', message: 'Copy failed.' });
        return;
      }
      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': image.blob })]);
      setSelectionCopyFeedback({ type: 'success', message: 'Copied PNG to clipboard.' });
    } catch (error) {
      console.error('Failed to copy graph selection:', error);
      setSelectionCopyFeedback({ type: 'error', message: 'Clipboard copy failed.' });
    }
  };

  const handleSnipSelection = async () => {
    if (!selectionRect) return;
    try {
      const rect = normalizeSelectionRect(selectionRect);
      const image = await renderSelectionImage(rect, { format: 'image/png' });
      if (!image) {
        setSelectionCopyFeedback({ type: 'error', message: 'Snip failed.' });
        return;
      }
      const snipItem = {
        id: `image-snip-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        kind: 'image',
        src: image.dataUrl,
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height,
        aspectRatio: rect.width / Math.max(1, rect.height),
        canvasPlacement: 'front',
      };
      ensureImageAsset(image.dataUrl);
      setAnnotationFuture([]);
      setAnnotationStrokes((current) => [...current, snipItem]);
      setSelectedAnnotationItemId(snipItem.id);
      setSelectionRect(null);
      setSelectionMenu(null);
      setSelectionResizeCursor('');
      setAnnotationTool('pointer');
      canvasWrapRef.current?.focus?.();
    } catch (error) {
      console.error('Failed to snip graph selection:', error);
      setSelectionCopyFeedback({ type: 'error', message: 'Snip failed.' });
    }
  };

  const handleExportSelection = async () => {
    if (!selectionRect) return;
    const image = await renderSelectionImage(selectionRect);
    if (!image) return;
    const link = document.createElement('a');
    link.download = 'graph-selection.jpeg';
    link.href = image.dataUrl;
    link.click();
  };

  const handleHandwritingPointerDown = (event) => {
    setActiveSurface('handwriting');
    const point = getHandwritingPoint(event);
    if (!point) return;
    if (annotationTool === 'pan') {
      handwritingRef.current.drawing = true;
      handwritingRef.current.currentStroke = {
        kind: 'surface-pan',
        startPoint: point,
        startOffset: handwritingOffset,
      };
      return;
    }
    if (annotationTool === 'text') {
      if (annotationTextMode === 'math') {
        beginMathDraft('handwriting', point, {
          left: event.clientX,
          top: event.clientY,
        });
      } else {
        beginTextDraft('handwriting', point);
      }
      return;
    }
    if (annotationTool === 'pointer') {
      const targetItem = findItemAtPoint(handwritingStrokes, point);
      if (targetItem) {
        handwritingRef.current.drawing = true;
        handwritingRef.current.currentStroke = {
          kind: 'item-drag',
          id: targetItem.id,
          offsetX: point.x - targetItem.x,
          offsetY: point.y - targetItem.y,
        };
      }
      return;
    }
    if (annotationTool === 'eraser') {
      const targetItem = findItemAtPoint(handwritingStrokes, point);
      if (targetItem) {
        setHandwritingStrokes((current) => current.filter((item) => item?.id !== targetItem.id));
        setHandwritingFuture((future) => [...future, targetItem]);
        return;
      }
      setHandwritingStrokes((current) => {
        if (!current.length) return current;
        const targetStrokeId = findStrokeAtPoint(current, point);
        if (!targetStrokeId) return current;
        const removedStroke = current.find((stroke) => stroke?.id === targetStrokeId);
        if (removedStroke) setHandwritingFuture((future) => [...future, removedStroke]);
        return current.filter((stroke) => stroke?.id !== targetStrokeId);
      });
      return;
    }
    handwritingRef.current.drawing = true;
    handwritingRef.current.currentStroke = {
      id: `handwriting-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      tool: annotationTool,
      points: [point],
    };
    setHandwritingFuture([]);
    setHandwritingStrokes((current) => [...current, handwritingRef.current.currentStroke]);
  };

  const handleHandwritingPointerMove = (event) => {
    const point = getHandwritingPoint(event);
    if (!point || !handwritingRef.current.drawing || !handwritingRef.current.currentStroke) return;
    if (handwritingRef.current.currentStroke.kind === 'surface-pan') {
      setHandwritingOffset({
        x: handwritingRef.current.currentStroke.startOffset.x - (point.canvasX - handwritingRef.current.currentStroke.startPoint.canvasX),
        y: handwritingRef.current.currentStroke.startOffset.y - (point.canvasY - handwritingRef.current.currentStroke.startPoint.canvasY),
      });
      return;
    }
    if (handwritingRef.current.currentStroke.kind === 'item-drag') {
      const { id, offsetX, offsetY } = handwritingRef.current.currentStroke;
      setHandwritingStrokes((current) =>
        current.map((item) =>
          item?.id === id && ['text', 'math'].includes(item.kind)
            ? { ...item, x: point.x - offsetX, y: point.y - offsetY }
            : item
        )
      );
      return;
    }
    const nextStroke = {
      ...handwritingRef.current.currentStroke,
      points: [...handwritingRef.current.currentStroke.points, point],
    };
    handwritingRef.current.currentStroke = nextStroke;
    setHandwritingStrokes((current) =>
      current.map((stroke) => (stroke?.id === nextStroke.id ? nextStroke : stroke))
    );
  };

  const endHandwritingStroke = () => {
    handwritingRef.current.drawing = false;
    handwritingRef.current.currentStroke = null;
  };

  const handleHandwritingUndo = () => {
    setHandwritingStrokes((current) => {
      if (!current.length) return current;
      const lastStroke = current[current.length - 1];
      setHandwritingFuture((future) => [...future, lastStroke]);
      return current.slice(0, -1);
    });
  };

  const handleHandwritingRedo = () => {
    setHandwritingFuture((future) => {
      if (!future.length) return future;
      const restoredStroke = future[future.length - 1];
      setHandwritingStrokes((current) => [...current, restoredStroke]);
      return future.slice(0, -1);
    });
  };

  const handleHandwritingClear = () => {
    if (!handwritingStrokes.length) return;
    setHandwritingFuture([]);
    setHandwritingStrokes([]);
  };

  const handleHandwritingDoubleClick = (event) => {
    if (annotationTool !== 'pointer') return;
    const point = getHandwritingPoint(event);
    if (!point) return;
    const targetItem = findItemAtPoint(handwritingStrokes, point);
    if (targetItem?.kind !== 'math') return;
    handwritingRef.current.drawing = false;
    handwritingRef.current.currentStroke = null;
    beginMathDraft(
      'handwriting',
      { x: targetItem.x, y: targetItem.y },
      { left: event.clientX, top: event.clientY },
      targetItem.latex || '',
      targetItem.id
    );
  };

  const handleActiveUndo = () => {
    if (activeSurface === 'handwriting') handleHandwritingUndo();
    else handleAnnotationUndo();
  };

  const handleActiveRedo = () => {
    if (activeSurface === 'handwriting') handleHandwritingRedo();
    else handleAnnotationRedo();
  };

  const adjustZoom = (direction) => {
    setZoomLevel((current) => {
      const next = direction === 'in' ? current + 0.25 : current - 0.25;
      return Math.max(0.5, Math.min(3, Number(next.toFixed(2))));
    });
  };

  useEffect(() => {
    if (annotationTool !== 'pointer') {
      clearSelection();
    }
  }, [annotationTool]);

  useEffect(() => {
    if (annotationTool !== 'text') {
      setTextDraft(null);
      setMathDraft(null);
    }
  }, [annotationTool]);

  useEffect(() => {
    if (isLibraryOpen) {
      loadSavedSketches();
    }
  }, [isLibraryOpen, loadSavedSketches]);

  useEffect(() => {
    if (!openLibrarySignal) return;
    setIsLibraryOpen(true);
  }, [openLibrarySignal]);

  useEffect(() => {
    if (!newCanvasSignal) return;
    startNewCanvas();
  }, [newCanvasSignal, startNewCanvas]);

  useEffect(() => {
    if (!openSchemaImportSignal) return;
    openSchemaImporter();
  }, [openSchemaImportSignal, openSchemaImporter]);

  return (
    <div
      ref={fullScreenRootRef}
      className={['bg-white', fullScreen ? 'flex h-full min-h-0 flex-col' : 'rounded-xl border border-gray-200 p-4 shadow-sm'].join(' ')}
    >
      {!fullScreen && (
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">GraphSketch Tool</h2>
            <p className="text-xs text-gray-500">Plot equations, tables, transformations, derivatives, inequalities, and inverses.</p>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full bg-sigma-50 px-3 py-1 text-xs font-medium text-sigma-700">
            <LineChart className="h-3.5 w-3.5" />
            Cartesian graphing
          </div>
        </div>
      )}
      {showSaveBar && (
        <div className={['rounded-xl border border-gray-200 bg-white/90 p-3', fullScreen ? 'mx-3 mb-3 mt-0' : 'mb-3'].join(' ')}>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center">
              <input
                type="text"
                value={sketchTitle}
                onChange={(event) => setSketchTitle(event.target.value)}
                placeholder={DEFAULT_SKETCH_TITLE}
                className="h-10 min-w-0 flex-1 rounded-lg border border-gray-300 px-3 text-sm text-gray-800 shadow-sm outline-none transition focus:border-sigma-400 focus:ring-2 focus:ring-sigma-100"
              />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={openSchemaImporter}
                  className="inline-flex h-10 items-center gap-2 rounded-lg border border-gray-300 px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <ClipboardPaste className="h-4 w-4" />
                  Import schema
                </button>
                <button
                  type="button"
                  onClick={() => setIsLibraryOpen(true)}
                  className="inline-flex h-10 items-center gap-2 rounded-lg border border-gray-300 px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <FolderOpen className="h-4 w-4" />
                  Open
                </button>
                <button
                  type="button"
                  onClick={() => saveSketch({ saveAsNew: true })}
                  disabled={isSavingSketch}
                  className="inline-flex h-10 items-center gap-2 rounded-lg border border-gray-300 px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  Save as new
                </button>
                <button
                  type="button"
                  onClick={() => saveSketch()}
                  disabled={isSavingSketch}
                  className="inline-flex h-10 items-center gap-2 rounded-lg bg-sigma-700 px-3 text-sm font-semibold text-white transition-colors hover:bg-sigma-800 disabled:cursor-not-allowed disabled:bg-sigma-300"
                >
                  <Save className="h-4 w-4" />
                  {currentSketchId ? 'Update' : 'Save'}
                </button>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              {saveFeedback.message || 'Use the tool during your trial. Upgrade to Basic or Pro when you need to save sketches.'}
            </div>
          </div>
        </div>
      )}
      <div className={[
        'grid min-h-0 flex-1 gap-4',
        fullScreen ? 'h-full' : 'min-h-[720px]',
        teachingMode
          ? 'grid-cols-1'
          : 'lg:grid-cols-[minmax(300px,1fr)_minmax(0,2fr)]',
      ].join(' ')}>
        {!teachingMode && (
        <aside
          className={[
            'min-h-0 h-full rounded-xl border border-gray-200 p-4 overflow-y-auto',
            activePanel === 'left' ? 'border-gray-200 bg-gray-50' : 'border-gray-200 bg-white shadow-sm',
          ].join(' ')}
        >
          <div className="mb-4 flex items-center justify-between gap-3 border-b border-gray-200 pb-3">
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {activePanel === 'left' ? 'Graph setup' : 'Insights'}
              </p>
              <p className="text-xs text-gray-500">
                {activePanel === 'left'
                  ? 'Configure equations, tables, and viewport.'
                  : 'Inspect graphs, transformations, and interpretation.'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {activePanel === 'left' && (
                <div className="relative" data-view-options-root>
                  <button
                    type="button"
                    onClick={() => setViewOptionsOpen((current) => !current)}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-900"
                    aria-label="View options"
                    aria-expanded={viewOptionsOpen}
                    aria-haspopup="dialog"
                  >
                    <Eye className="h-4.5 w-4.5" />
                  </button>
                  {viewOptionsOpen && (
                    <div className="absolute right-0 top-full z-30 mt-2 w-56 rounded-xl border border-gray-200 bg-white p-3 shadow-xl">
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">View options</p>
                      <div className="mt-3 space-y-3">
                        <label className="flex items-center justify-between gap-3 text-sm font-medium text-gray-700">
                          <span>Axis labels</span>
                          <input
                            type="checkbox"
                            checked={showAxisLabels}
                            onChange={(event) => setShowAxisLabels(event.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-sigma-700 focus:ring-sigma-500"
                          />
                        </label>
                        <label className="flex items-center justify-between gap-3 text-sm font-medium text-gray-700">
                          <span>Axis markers</span>
                          <input
                            type="checkbox"
                            checked={showAxisMarkers}
                            onChange={(event) => setShowAxisMarkers(event.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-sigma-700 focus:ring-sigma-500"
                          />
                        </label>
                        <label className="flex items-center justify-between gap-3 text-sm font-medium text-gray-700">
                          <span>Canvas toolbar</span>
                          <input
                            type="checkbox"
                            checked={showCanvasToolbar}
                            onChange={(event) => setShowCanvasToolbar(event.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-sigma-700 focus:ring-sigma-500"
                          />
                        </label>
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-gray-700">Canvas background</p>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={getColorInputValue(canvasBackgroundColor)}
                              onChange={(event) => updateCanvasBackgroundColor(event.target.value)}
                              className="h-10 w-10 cursor-pointer rounded-lg border border-gray-300 bg-white p-1"
                              aria-label="Pick canvas background color"
                              title="Pick canvas background color"
                            />
                            <input
                              type="text"
                              value={canvasBackgroundInput}
                              onChange={(event) => handleCanvasBackgroundInputChange(event.target.value)}
                              onBlur={handleCanvasBackgroundInputBlur}
                              placeholder="#FFFFFF or rgb(255, 255, 255)"
                              className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 shadow-sm outline-none transition focus:border-sigma-400 focus:ring-2 focus:ring-sigma-100"
                              aria-label="Canvas background color"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-gray-700">Grid style</p>
                          <div className="space-y-1.5">
                            {GRID_MODE_OPTIONS.map((option) => (
                              <button
                                key={option.id}
                                type="button"
                                onClick={() => setGridMode(option.id)}
                                className={[
                                  'w-full rounded-lg border px-3 py-2 text-left transition-colors',
                                  gridMode === option.id
                                    ? 'border-sigma-600 bg-sigma-50 text-sigma-900'
                                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50',
                                ].join(' ')}
                              >
                                <div className="text-sm font-medium">{option.label}</div>
                                <div className="mt-0.5 text-xs text-gray-500">{option.description}</div>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="inline-flex rounded-xl border border-gray-200 bg-gray-100 p-1">
                <button
                  type="button"
                  onClick={() => setActivePanel('left')}
                  className={[
                    'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    activePanel === 'left' ? 'bg-white text-sigma-800 shadow-sm' : 'text-gray-600 hover:text-gray-900',
                  ].join(' ')}
                >
                  Setup
                </button>
                <button
                  type="button"
                  onClick={() => setActivePanel('right')}
                  className={[
                    'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    activePanel === 'right' ? 'bg-white text-sigma-800 shadow-sm' : 'text-gray-600 hover:text-gray-900',
                  ].join(' ')}
                >
                  Insights
                </button>
              </div>
            </div>
          </div>
          {activePanel === 'left' ? (
          <div className="space-y-5">
            <section className="space-y-3">
              <div className="inline-flex rounded-xl border border-gray-200 bg-gray-100 p-1">
                <button
                  type="button"
                  onClick={() => setSetupMode('equation')}
                  className={[
                    'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    setupMode === 'equation' ? 'bg-white text-sigma-800 shadow-sm' : 'text-gray-600 hover:text-gray-900',
                  ].join(' ')}
                >
                  Equation sketch
                </button>
                <button
                  type="button"
                  onClick={() => setSetupMode('table')}
                  className={[
                    'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    setupMode === 'table' ? 'bg-white text-sigma-800 shadow-sm' : 'text-gray-600 hover:text-gray-900',
                  ].join(' ')}
                >
                  Table plotting
                </button>
              </div>

              {setupMode === 'equation' ? (
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-gray-900">Plot from an equation</p>
                  <div className="inline-flex rounded-xl border border-gray-200 bg-gray-100 p-1">
                    {EQUATION_GROUP_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => changeEquationGroup(option.id)}
                        className={[
                          'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                          equationGroup === option.id ? 'bg-white text-sigma-800 shadow-sm' : 'text-gray-600 hover:text-gray-900',
                        ].join(' ')}
                        aria-pressed={equationGroup === option.id}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <div className="relative" data-equation-dropdown="root">
                    <button
                      type="button"
                      onClick={() => setEquationDropdownOpen((current) => !current)}
                      className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm shadow-sm"
                      aria-expanded={equationDropdownOpen}
                      aria-haspopup="listbox"
                    >
                      <LatexText latex={EQUATIONS[equationType].latex} className="text-gray-900" />
                      <ChevronDown
                        className={[
                          'h-4 w-4 text-gray-500 transition-transform',
                          equationDropdownOpen ? 'rotate-180' : '',
                        ].join(' ')}
                      />
                    </button>
                    {equationDropdownOpen && (
                      <div className="absolute z-20 mt-2 max-h-80 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white p-1 shadow-xl">
                        {equationTypesForGroup(equationGroup).map((key) => (
                          <button
                            key={key}
                            type="button"
                            onClick={() => {
                              setEquationType(key);
                              setEquationParams(makeParams(key));
                              setEquationDropdownOpen(false);
                            }}
                            className={[
                              'flex w-full items-center rounded-lg px-3 py-2 text-left text-sm transition-colors',
                              key === equationType
                                ? 'bg-sigma-700 text-white'
                                : 'text-gray-800 hover:bg-gray-100',
                            ].join(' ')}
                            role="option"
                            aria-selected={key === equationType}
                          >
                            <LatexText latex={EQUATIONS[key].latex} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="rounded-lg border border-dashed border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600">
                    <LatexText latex={EQUATIONS[equationType].latex} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {EQUATIONS[equationType].params.map((param) => (
                      <label key={param} className="block text-sm font-medium text-gray-700">
                        <LatexText latex={param} />
                        <input type="number" value={equationParams[param]} onChange={(event) => setEquationParams((current) => ({ ...current, [param]: event.target.value }))} className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" />
                      </label>
                    ))}
                  </div>
                  <input type="text" value={equationTitle} onChange={(event) => setEquationTitle(event.target.value)} placeholder="Optional graph title" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" />
                  <button type="button" onClick={addEquationGraph} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-sigma-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sigma-800">
                    <Plus className="h-4 w-4" />
                    Add graph
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-gray-900">Plot from a table</p>
                  <input type="text" value={tableTitle} onChange={(event) => setTableTitle(event.target.value)} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" />
                  <div className="space-y-2">
                    {tableRows.map((row, index) => (
                      <div key={`row-${index + 1}`} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                        <input type="number" value={row.x} onChange={(event) => setTableRows((current) => current.map((entry, entryIndex) => entryIndex === index ? { ...entry, x: event.target.value } : entry))} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" placeholder="x" />
                        <input type="number" value={row.y} onChange={(event) => setTableRows((current) => current.map((entry, entryIndex) => entryIndex === index ? { ...entry, y: event.target.value } : entry))} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" placeholder="y" />
                        <button type="button" onClick={() => setTableRows((current) => current.filter((_, entryIndex) => entryIndex !== index))} className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-100" aria-label="Delete row">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <button type="button" onClick={() => setTableRows((current) => [...current, { x: '', y: '' }])} className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">
                      <Plus className="h-4 w-4" />
                      Add row
                    </button>
                    <button type="button" onClick={addTableGraph} className="inline-flex items-center justify-center gap-2 rounded-lg bg-sigma-700 px-3 py-2 text-sm font-semibold text-white hover:bg-sigma-800">
                      <LineChart className="h-4 w-4" />
                      Plot table
                    </button>
                  </div>
                </div>
              )}
            </section>

            <section className="space-y-3 border-t border-gray-200 pt-4">
              <p className="text-sm font-semibold text-gray-900">Graph selection</p>
              <div className="relative" data-graph-dropdown="root">
                <button
                  type="button"
                  onClick={() => setGraphDropdownOpen((current) => !current)}
                  className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm shadow-sm"
                  aria-expanded={graphDropdownOpen}
                  aria-haspopup="listbox"
                >
                  {selectedGraph ? (
                    <LatexText
                      latex={graphLatex(selectedGraph, graphMap)}
                      className="text-gray-900"
                    />
                  ) : (
                    <span className="text-gray-500">Select active graph</span>
                  )}
                  <ChevronDown
                    className={[
                      'h-4 w-4 text-gray-500 transition-transform',
                      graphDropdownOpen ? 'rotate-180' : '',
                    ].join(' ')}
                  />
                </button>
                {graphDropdownOpen && (
                  <div className="absolute z-20 mt-2 max-h-80 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white p-1 shadow-xl">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedGraphId('');
                        setGraphDropdownOpen(false);
                      }}
                      className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-gray-500 transition-colors hover:bg-gray-100"
                    >
                      Select active graph
                    </button>
                    {graphs.map((graph) => (
                      <button
                        key={graph.id}
                        type="button"
                        onClick={() => {
                          setSelectedGraphId(graph.id);
                          setGraphDropdownOpen(false);
                        }}
                        className={[
                          'flex w-full items-center rounded-lg px-3 py-2 text-left text-sm transition-colors',
                          graph.id === selectedGraphId
                            ? 'bg-sigma-700 text-white'
                            : 'text-gray-800 hover:bg-gray-100',
                        ].join(' ')}
                        role="option"
                        aria-selected={graph.id === selectedGraphId}
                      >
                        <LatexText latex={graphLatex(graph, graphMap)} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="rounded-lg border border-dashed border-gray-300 bg-white px-3 py-3 text-sm text-gray-700">
                <p className="font-medium text-gray-900">Active graph definition</p>
                <div className="mt-1 text-gray-600">
                  <InlineLatexRenderer latex={activeGraphDefinition} className="text-gray-600" />
                </div>
              </div>
              {selectedGraph && (
                <div className="rounded-lg border border-gray-200 bg-white px-3 py-3">
                  <p className="text-sm font-medium text-gray-900">Graph color</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {GRAPH_COLOR_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => updateSelectedGraphColor(option.value)}
                        className={[
                          'inline-flex h-9 w-9 items-center justify-center rounded-full border-2 transition-transform hover:scale-105',
                          selectedGraph.color === option.value ? 'border-slate-900' : 'border-white',
                        ].join(' ')}
                        style={{ backgroundColor: option.value }}
                        title={option.label}
                        aria-label={`Set graph color to ${option.label}`}
                      >
                        {selectedGraph.color === option.value && <span className="h-2.5 w-2.5 rounded-full bg-white shadow-sm" />}
                      </button>
                    ))}
                  </div>
                  <p className="mt-4 text-sm font-medium text-gray-900">Line style</p>
                  <div className="mt-3 inline-flex rounded-lg border border-gray-200 bg-gray-100 p-1">
                    {GRAPH_LINE_STYLE_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => updateSelectedGraphLineStyle(option.id)}
                        className={[
                          'rounded-md px-3 py-2 text-xs font-medium transition-colors',
                          (selectedGraph.lineStyle || 'solid') === option.id
                            ? 'bg-white text-sigma-800 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900',
                        ].join(' ')}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </section>

            <section className="space-y-3 border-t border-gray-200 pt-4">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-900">Active graphs</p>
                <span className="rounded-full bg-sigma-50 px-2.5 py-1 text-xs font-medium text-sigma-700">{graphs.length} graph{graphs.length === 1 ? '' : 's'}</span>
              </div>
              <div className="space-y-2">
                {graphs.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-500">Add an equation graph or a table plot to begin sketching.</div>
                ) : (
                  graphs.map((graph) => (
                    <div
                      key={graph.id}
                      className={[
                        'rounded-lg border px-3 py-3',
                        graph.id === selectedGraphId ? 'border-sigma-300 bg-sigma-50' : 'border-gray-200 bg-white',
                        graph.visible === false ? 'opacity-60' : '',
                      ].join(' ')}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={graph.visible ?? true}
                              onChange={() => toggleGraphVisibility(graph.id)}
                              className="h-4 w-4 rounded border-gray-300 text-sigma-700 focus:ring-sigma-500"
                              aria-label={`${graph.visible === false ? 'Show' : 'Hide'} ${graph.title}`}
                            />
                            <span className="h-3 w-3 rounded-full" style={{ backgroundColor: graph.color }} />
                            <InlineLatexRenderer
                              latex={graphLatex(graph, graphMap)}
                              className="min-w-0 truncate text-sm font-semibold text-gray-900"
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">{graph.kind}</p>
                        </div>
                        <button type="button" onClick={() => removeGraph(graph.id)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-100" aria-label={`Delete ${graph.title}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="mt-3 grid gap-2 sm:grid-cols-2">
                        <button type="button" onClick={() => setSelectedGraphId(graph.id)} className={['rounded-lg border px-3 py-2 text-xs font-medium', graph.id === selectedGraphId ? 'border-sigma-600 bg-sigma-100 text-sigma-800' : 'border-gray-300 bg-white text-gray-700'].join(' ')}>Use as f(x)</button>
                        <button type="button" onClick={() => setCompareGraphId(graph.id)} className={['rounded-lg border px-3 py-2 text-xs font-medium', graph.id === compareGraphId ? 'border-sigma-600 bg-sigma-100 text-sigma-800' : 'border-gray-300 bg-white text-gray-700'].join(' ')}>Use as g(x)</button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>

            <section className="space-y-3 border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-900">Viewport</p>
                <button
                  type="button"
                  onClick={() => {
                    const resetViewport = equationGroup === 'trig' ? TRIG_VIEWPORT : DEFAULT_VIEWPORT;
                    setViewport(resetViewport);
                    setAxisStep(1);
                    setXAxisMarker(equationGroup === 'trig' ? 'degree30' : 'number');
                  }}
                  className="inline-flex items-center gap-1 text-xs font-medium text-sigma-700 hover:text-sigma-900"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Reset
                </button>
              </div>
              <div>
                <p className="mb-1 text-xs font-semibold uppercase text-gray-500">Units per marker</p>
                <div className="grid grid-cols-3 gap-2">
                  {AXIS_STEP_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setAxisStep(option.value)}
                      className={[
                        'rounded-lg border px-3 py-2 text-sm font-semibold transition-colors',
                        axisStep === option.value
                          ? 'border-sigma-700 bg-sigma-700 text-white'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
                      ].join(' ')}
                      aria-pressed={axisStep === option.value}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-1 text-xs font-semibold uppercase text-gray-500">X-axis markers</p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {X_AXIS_MARKER_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setXAxisMarker(option.id)}
                      className={[
                        'rounded-lg border px-3 py-2 text-sm font-semibold transition-colors',
                        xAxisMarker === option.id
                          ? 'border-sigma-700 bg-sigma-700 text-white'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
                      ].join(' ')}
                      aria-pressed={xAxisMarker === option.id}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(viewport).map(([key, value]) => (
                  <label key={key} className="block text-sm font-medium capitalize text-gray-700">
                    {key}
                    <input type="number" value={value} onChange={(event) => setViewport((current) => ({ ...current, [key]: num(event.target.value, current[key]) }))} className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" />
                  </label>
                ))}
              </div>
            </section>
          </div>
          ) : (
          <div className="space-y-4">
            <section className="space-y-3">
              <p className="text-sm font-semibold text-gray-900">Transformations, derivatives, and inverses</p>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                <span className="font-medium text-gray-900">Selected graph: </span>
                {selectedGraph ? (
                  <LatexText latex={graphLatex(selectedGraph, graphMap)} className="font-semibold text-gray-900" />
                ) : (
                  <span className="font-semibold text-gray-900">Choose a graph</span>
                )}
              </div>
              <div className="inline-flex rounded-xl border border-gray-200 bg-gray-100 p-1">
                <button
                  type="button"
                  onClick={() => setTransform((current) => ({ ...current, mode: 'copy' }))}
                  className={[
                    'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    transform.mode === 'copy' ? 'bg-white text-sigma-800 shadow-sm' : 'text-gray-600 hover:text-gray-900',
                  ].join(' ')}
                >
                  Create copy
                </button>
                <button
                  type="button"
                  onClick={() => setTransform((current) => ({ ...current, mode: 'apply' }))}
                  className={[
                    'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    transform.mode === 'apply' ? 'bg-white text-sigma-800 shadow-sm' : 'text-gray-600 hover:text-gray-900',
                  ].join(' ')}
                >
                  Apply to graph
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="block text-sm font-medium text-gray-700"><LatexText latex={'f(x+k)'} /><input type="number" value={transform.horizontalShift} onChange={(event) => setTransform((current) => ({ ...current, horizontalShift: event.target.value }))} className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" /></label>
                <label className="block text-sm font-medium text-gray-700"><LatexText latex={'f(x)+k'} /><input type="number" value={transform.verticalShift} onChange={(event) => setTransform((current) => ({ ...current, verticalShift: event.target.value }))} className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" /></label>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700"><input type="checkbox" checked={transform.reflectX} onChange={(event) => setTransform((current) => ({ ...current, reflectX: event.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-sigma-700" /><LatexText latex={'-f(x)'} /></label>
                <label className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700"><input type="checkbox" checked={transform.reflectY} onChange={(event) => setTransform((current) => ({ ...current, reflectY: event.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-sigma-700" /><LatexText latex={'f(-x)'} /></label>
              </div>
              <div className="grid gap-2 sm:grid-cols-3">
                <button type="button" onClick={addTransformGraph} disabled={!selectedGraph} className="inline-flex items-center justify-center gap-2 rounded-lg bg-sigma-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sigma-800 disabled:cursor-not-allowed disabled:bg-gray-300"><CopyPlus className="h-4 w-4" />{transform.mode === 'apply' ? 'Apply transform' : 'Add transform'}</button>
                <button type="button" onClick={addDerivativeGraph} disabled={!selectedGraph} className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:bg-gray-100"><LineChart className="h-4 w-4" />Add derivative</button>
                <button type="button" onClick={addInverseGraph} disabled={!selectedGraph} className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:bg-gray-100"><ArrowDownUp className="h-4 w-4" />Add inverse</button>
              </div>
            </section>

            <section className="space-y-3 border-t border-gray-200 pt-4">
              <p className="text-sm font-semibold text-gray-900">Visual interpretation</p>
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="rounded-lg border border-gray-200 bg-white px-3 py-3 text-sm text-gray-700">
                  <p className="font-medium text-gray-900">Derivative</p>
                  <div className="mt-1 text-gray-600">
                    <LatexText latex={derivativeInsightLatex} />
                  </div>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white px-3 py-3 text-sm text-gray-700">
                  <p className="font-medium text-gray-900">Inverse</p>
                  <div className="mt-1 text-gray-600">
                    <LatexText latex={inverseInsightLatex} />
                  </div>
                </div>
              </div>
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-3 text-sm text-gray-700">
                <p className="font-medium text-gray-900">Active insight</p>
                <p className="mt-1 text-gray-600">
                  <LatexText latex={ANALYSIS.find(([id]) => id === analysisMode)?.[2] || '\\text{No insight selected}'} />
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {ANALYSIS.map(([id, label, latex]) => (
                  <button key={id} type="button" onClick={() => setAnalysisMode((current) => (current === id ? '' : id))} className={['rounded-lg border px-3 py-2 text-left text-xs font-medium', analysisMode === id ? 'border-sigma-600 bg-sigma-100 text-sigma-800' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'].join(' ')}>
                    <LatexText latex={latex || label} />
                  </button>
                ))}
              </div>
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-3 text-sm text-gray-700">
                <p className="font-medium text-gray-900">Current view summary</p>
                <p className="mt-1 text-gray-600">{summary}</p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white px-3 py-3 text-xs text-gray-500">Blue shading marks intervals in the current window where the selected condition is true. Use f(x + k), f(x) + k, -f(x), f(-x), derivatives, products, quotients, and inverse reflections to study transformations.</div>
            </section>
          </div>
          )}
        </aside>
        )}

        <main className="flex min-h-0 min-w-0 h-full flex-col">
          <div className="relative min-h-0 min-w-0 flex-1 rounded-xl border border-gray-200 bg-gray-50 p-3">
            <button
              type="button"
              onClick={() => setTeachingMode((current) => !current)}
              className={[
                'absolute left-0 top-0 z-20 inline-flex items-center gap-2 rounded-br-xl rounded-tl-xl border border-gray-200 bg-white/95 px-3 py-2 text-sm font-medium shadow-lg backdrop-blur transition-colors',
                teachingMode ? 'border-sigma-600 bg-sigma-50/95 text-sigma-800' : 'text-gray-700 hover:bg-gray-50',
              ].join(' ')}
            >
              <PenTool className="h-4 w-4" />
              {teachingMode ? 'Exit teaching mode' : 'Teaching mode'}
            </button>
            <div className={['flex h-full min-h-0 gap-3', teachingMode ? 'flex-col lg:flex-row' : 'flex-col'].join(' ')}>
              <div
                ref={canvasWrapRef}
                className="relative min-h-0 min-w-0 flex-1 outline-none"
                tabIndex={0}
                onKeyDown={handleCanvasKeyDown}
                onPaste={handleCanvasPaste}
                onDragOver={handleCanvasDragOver}
                onDrop={handleCanvasDrop}
              >
                {showCanvasToolbar && (
                <div className="absolute right-0 top-0 z-20 inline-flex rounded-bl-xl rounded-tr-xl border border-gray-200 bg-white/95 p-1 shadow-lg backdrop-blur">
                  {[
                    ['pointer', MousePointer2, 'Pointer'],
                    ['pan', Hand, 'Pan'],
                    ['text', Type, 'Text'],
                    ['pen', PenTool, 'Pen'],
                    ['highlighter', Highlighter, 'Highlighter'],
                    ['eraser', Eraser, 'Eraser'],
                  ].map(([toolId, Icon, label]) => (
                    <button
                      key={toolId}
                      type="button"
                      onClick={() => setAnnotationTool(toolId)}
                      className={[
                        'inline-flex h-10 w-10 items-center justify-center rounded-lg transition-colors',
                        annotationTool === toolId ? 'bg-sigma-700 text-white' : 'text-gray-600 hover:bg-gray-100',
                      ].join(' ')}
                      title={label}
                      aria-label={label}
                    >
                      <Icon className="h-4 w-4" />
                    </button>
                  ))}
                  <div className="mx-1 w-px self-stretch bg-gray-200" />
                  <button
                    type="button"
                    onClick={handleActiveUndo}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                    title="Undo"
                    aria-label="Undo"
                    disabled={activeUndoDisabled}
                  >
                    <Undo2 className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={handleActiveRedo}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                    title="Redo"
                    aria-label="Redo"
                    disabled={activeRedoDisabled}
                  >
                    <Redo2 className="h-4 w-4" />
                  </button>
                </div>
                )}
                {annotationTool === 'text' && (
                  <div className="absolute left-0 top-0 z-20 flex items-center gap-2 rounded-br-xl rounded-tl-xl border border-gray-200 bg-white/95 p-1.5 shadow-lg backdrop-blur">
                    <div className="inline-flex rounded-md border border-gray-200 bg-gray-100 p-0.5">
                      {TEXT_MODE_OPTIONS.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => setAnnotationTextMode(option.id)}
                          className={[
                            'rounded px-2 py-1 text-xs font-medium transition-colors',
                            annotationTextMode === option.id ? 'bg-white text-sigma-800 shadow-sm' : 'text-gray-600 hover:text-gray-900',
                          ].join(' ')}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    {TEXT_SIZE_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => setAnnotationTextSize(option.size)}
                        className={[
                          'inline-flex h-8 min-w-8 items-center justify-center rounded-md px-2 text-xs font-semibold transition-colors',
                          selectedTextSizeId === option.id ? 'bg-sigma-700 text-white' : 'text-gray-600 hover:bg-gray-100',
                        ].join(' ')}
                        aria-label={`Text size ${option.id}`}
                      >
                        {option.id}
                      </button>
                    ))}
                    <div className="mx-0.5 w-px self-stretch bg-gray-200" />
                    <button
                      type="button"
                      onClick={() => addGraphMarker('arrow')}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-100"
                      aria-label="Add end arrow"
                      title="Add a movable end arrow"
                    >
                      <ArrowRight className="h-4 w-4" />
                      Arrow
                    </button>
                    <button
                      type="button"
                      onClick={() => addGraphMarker('dot')}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-100"
                      aria-label="Add graph dot"
                      title="Add a movable graph dot"
                    >
                      <Circle className="h-3.5 w-3.5 fill-current" />
                      Dot
                    </button>
                  </div>
                )}
                {selectedAnnotationItem && ['arrow', 'dot'].includes(selectedAnnotationItem.kind) && (
                  <div className="absolute left-0 top-14 z-20 flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white/95 p-1.5 shadow-lg backdrop-blur">
                    <span className="px-2 text-xs font-medium text-gray-600">
                      {selectedAnnotationItem.kind === 'arrow' ? 'Arrow' : 'Dot'} style
                    </span>
                    {selectedAnnotationItem.kind === 'arrow' && (
                      <>
                        <label className="flex items-center gap-1 text-xs font-medium text-gray-500" title="Drag the round canvas handle for free rotation">
                          Angle
                          <input
                            type="number"
                            value={Math.round(selectedAnnotationItem.rotation || 0)}
                            onChange={(event) => updateSelectedMarker({ rotation: Number(event.target.value) || 0 })}
                            className="h-8 w-16 rounded-md border border-gray-200 bg-white px-2 text-right text-xs text-gray-700 outline-none focus:border-sigma-400"
                            aria-label="Arrow rotation in degrees"
                          />
                          °
                        </label>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => updateSelectedMarker({
                        color: selectedGraph?.color || '#1E293B',
                        graphId: selectedGraph?.id || '',
                        lineWidth: selectedGraph ? 3 : 2,
                        lineStyle: selectedGraph?.lineStyle || 'solid',
                      })}
                      className="inline-flex h-8 items-center gap-2 rounded-md px-2 text-xs font-semibold text-gray-600 hover:bg-gray-100"
                      title={selectedGraph ? 'Use the active graph color' : 'Use the default graph color'}
                    >
                      <span
                        className="h-3.5 w-3.5 rounded-full border border-white shadow ring-1 ring-gray-300"
                        style={{ backgroundColor: selectedGraph?.color || '#1E293B' }}
                      />
                      Graph style
                    </button>
                  </div>
                )}
                {selectedAnnotationItem?.kind === 'image' && (
                  <div className="absolute left-0 top-14 z-20 flex items-center gap-2 rounded-xl border border-gray-200 bg-white/95 p-1.5 shadow-lg backdrop-blur">
                    <div className="flex items-center gap-2 px-2 text-xs font-medium text-gray-600">
                      <Layers className="h-3.5 w-3.5" />
                      Image layer
                    </div>
                    <button
                      type="button"
                      onClick={() => updateSelectedImagePlacement('behind')}
                      className={[
                        'rounded-lg px-3 py-2 text-xs font-semibold transition-colors',
                        selectedImagePlacement === 'behind'
                          ? 'bg-sigma-700 text-white'
                          : 'text-gray-600 hover:bg-gray-100',
                      ].join(' ')}
                    >
                      Behind canvas
                    </button>
                    <button
                      type="button"
                      onClick={() => updateSelectedImagePlacement('front')}
                      className={[
                        'rounded-lg px-3 py-2 text-xs font-semibold transition-colors',
                        selectedImagePlacement === 'front'
                          ? 'bg-sigma-700 text-white'
                          : 'text-gray-600 hover:bg-gray-100',
                      ].join(' ')}
                    >
                      In front
                    </button>
                  </div>
                )}
                <canvas
                  ref={canvasRef}
                  className="absolute inset-0 h-full w-full rounded-lg border border-gray-200"
                  style={{ backgroundColor: canvasBackgroundColor }}
                />
                <canvas
                  ref={annotationCanvasRef}
                  className="absolute inset-0 h-full w-full rounded-lg"
                  style={{ cursor: selectionResizeCursor || TOOL_CURSORS[annotationTool] || 'auto' }}
                  onPointerDown={handleAnnotationPointerDown}
                  onPointerMove={handleAnnotationPointerMove}
                  onPointerUp={endAnnotationStroke}
                  onPointerLeave={clearToolCursor}
                  onPointerEnter={handleAnnotationPointerMove}
                  onDoubleClick={handleAnnotationDoubleClick}
                />
                {textDraft?.surface === 'graph' && (
                  <input
                    ref={textInputRef}
                    type="text"
                    value={textDraft.text}
                    onChange={(event) => setTextDraft((current) => (current ? { ...current, text: event.target.value } : current))}
                    onBlur={commitTextDraft}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') commitTextDraft();
                      if (event.key === 'Escape') setTextDraft(null);
                      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'm') {
                        event.preventDefault();
                        beginMathDraft(
                          'graph',
                          { x: textDraft.x, y: textDraft.y },
                          event.currentTarget.getBoundingClientRect(),
                          textDraft.text.trim().replace(/^\$(.*)\$$/, '$1')
                        );
                        setTextDraft(null);
                        setAnnotationTextMode('math');
                      }
                    }}
                    className="absolute z-30 min-w-40 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-md"
                    style={{
                      left: textDraft.x + 12,
                      top: textDraft.y + 12,
                      fontSize: `${textDraft.size}px`,
                    }}
                    placeholder="Add text"
                  />
                )}
                <InlineMathModal
                  isOpen={Boolean(mathDraft)}
                  initialLatex={mathDraft?.initialLatex || ''}
                  anchorRect={mathDraft?.anchorRect || null}
                  onInsert={commitMathDraft}
                  onClose={() => setMathDraft(null)}
                />
                {selectionMenu && selectionRect && (
                  <div
                    className="absolute z-30 w-44 rounded-xl border border-gray-200 bg-white p-2 shadow-xl"
                    style={{ left: selectionMenu.left + 12, top: selectionMenu.top + 12 }}
                  >
                    {selectionCopyFeedback.type !== 'idle' && (
                      <div
                        className={[
                          'mb-2 rounded-lg px-3 py-2 text-xs font-medium',
                          selectionCopyFeedback.type === 'success'
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-rose-50 text-rose-700',
                        ].join(' ')}
                      >
                        {selectionCopyFeedback.message}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={handleSnipSelection}
                      className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
                    >
                      <Scissors className="h-4 w-4" />
                      Snip to canvas
                    </button>
                    <button
                      type="button"
                      onClick={handleCopySelection}
                      className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={typeof navigator === 'undefined' || !navigator.clipboard?.write || typeof window.ClipboardItem === 'undefined'}
                    >
                      <Copy className="h-4 w-4" />
                      Copy PNG
                    </button>
                    <button
                      type="button"
                      onClick={handleExportSelection}
                      className="mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
                    >
                      <Download className="h-4 w-4" />
                      Export JPEG
                    </button>
                    <button
                      type="button"
                      onClick={clearSelection}
                      className="mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100"
                    >
                      <Trash2 className="h-4 w-4" />
                      Clear selection
                    </button>
                  </div>
                )}
                <div className="absolute bottom-0 right-0 z-20 inline-flex items-center gap-1 rounded-tl-xl border border-gray-200 bg-white/95 p-1 shadow-lg backdrop-blur">
                  <button
                    type="button"
                    onClick={() => adjustZoom('out')}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100"
                    aria-label="Zoom out"
                    title="Zoom out"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setZoomLevel(1)}
                    className="min-w-[3.5rem] rounded-lg px-2 py-2 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-100"
                    aria-label="Reset zoom"
                    title="Reset zoom"
                  >
                    {Math.round(zoomLevel * 100)}%
                  </button>
                  <button
                    type="button"
                    onClick={() => adjustZoom('in')}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100"
                    aria-label="Zoom in"
                    title="Zoom in"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <div className="absolute bottom-0 left-0 z-20 inline-flex items-center gap-1 rounded-tr-xl border border-gray-200 bg-white/95 p-1 shadow-lg backdrop-blur">
                  <button
                    type="button"
                    onClick={() => activeSlideIndex > 0 && switchToSlide(slides[activeSlideIndex - 1]?.id)}
                    disabled={activeSlideIndex <= 0}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Previous canvas"
                    title="Previous canvas"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <div
                    className="min-w-[2.5rem] px-1 text-center text-sm font-semibold text-[#ed1566]"
                    aria-label={`Canvas ${activeSlideIndex >= 0 ? activeSlideIndex + 1 : 1} of ${slides.length || 1}`}
                  >
                    {activeSlideIndex >= 0 ? activeSlideIndex + 1 : 1}
                  </div>
                  <button
                    type="button"
                    onClick={() => activeSlideIndex >= 0 && activeSlideIndex < slides.length - 1 && switchToSlide(slides[activeSlideIndex + 1]?.id)}
                    disabled={activeSlideIndex < 0 || activeSlideIndex >= slides.length - 1}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Next canvas"
                    title="Next canvas"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                  <div className="mx-1 w-px self-stretch bg-gray-200" />
                  <button
                    type="button"
                    onClick={addSlide}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100"
                    aria-label="Add canvas"
                    title="Add canvas"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={duplicateSlide}
                    disabled={!activeSlide}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Duplicate canvas"
                    title="Duplicate canvas"
                  >
                    <CopyPlus className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => activeSlide && removeSlide(activeSlide.id)}
                    disabled={!activeSlide}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Delete canvas"
                    title="Delete canvas"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {teachingMode && (
                <div className="flex min-h-[280px] flex-col rounded-lg border border-gray-200 bg-white lg:min-h-0 lg:w-1/3 lg:max-w-[33.333%]">
                  <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Handwriting panel</p>
                      <p className="text-xs text-gray-500">Quick pen space for worked solutions and notes.</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 text-xs font-medium text-gray-600">
                        <input
                          type="checkbox"
                          checked={showHandwritingGuides}
                          onChange={(event) => setShowHandwritingGuides(event.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-sigma-700 focus:ring-sigma-500"
                        />
                        Margin lines
                      </label>
                      <p className="text-xs font-medium text-gray-500">Uses the main canvas toolbar.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
                    <button
                      type="button"
                      onClick={handleHandwritingUndo}
                      disabled={!handwritingStrokes.length}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                      title="Undo handwriting"
                    >
                      <Undo2 className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={handleHandwritingRedo}
                      disabled={!handwritingFuture.length}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                      title="Redo handwriting"
                    >
                      <Redo2 className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={handleHandwritingClear}
                      className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
                    >
                      <Trash2 className="h-4 w-4" />
                      Clear
                    </button>
                  </div>
                  <div ref={handwritingWrapRef} className="relative min-h-0 flex-1 p-4">
                    <canvas
                      ref={handwritingCanvasRef}
                      className="h-full w-full rounded-lg border border-gray-200 bg-white"
                      style={{ cursor: TOOL_CURSORS[annotationTool] || 'auto' }}
                      onPointerDown={handleHandwritingPointerDown}
                      onPointerMove={handleHandwritingPointerMove}
                      onPointerUp={endHandwritingStroke}
                      onPointerLeave={endHandwritingStroke}
                      onDoubleClick={handleHandwritingDoubleClick}
                    />
                    {textDraft?.surface === 'handwriting' && (
                      <input
                        ref={textInputRef}
                        type="text"
                        value={textDraft.text}
                        onChange={(event) => setTextDraft((current) => (current ? { ...current, text: event.target.value } : current))}
                        onBlur={commitTextDraft}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') commitTextDraft();
                          if (event.key === 'Escape') setTextDraft(null);
                          if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'm') {
                            event.preventDefault();
                            beginMathDraft(
                              'handwriting',
                              { x: textDraft.x, y: textDraft.y },
                              event.currentTarget.getBoundingClientRect(),
                              textDraft.text.trim().replace(/^\$(.*)\$$/, '$1')
                            );
                            setTextDraft(null);
                            setAnnotationTextMode('math');
                          }
                        }}
                        className="absolute z-30 min-w-40 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-md"
                        style={{
                          left: textDraft.x - handwritingOffset.x + 12,
                          top: textDraft.y - handwritingOffset.y + 12,
                          fontSize: `${textDraft.size}px`,
                        }}
                        placeholder="Add text"
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
      {isSchemaImportOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="graph-schema-import-title"
        >
          <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
              <div>
                <h3 id="graph-schema-import-title" className="text-lg font-semibold text-gray-900">
                  Import graph schema
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Paste a full version-2 graph document. Importing replaces the current canvas but remains unsaved until you click Save.
                </p>
              </div>
              <button
                type="button"
                onClick={closeSchemaImporter}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-300 text-gray-500 transition-colors hover:bg-gray-50"
                aria-label="Close schema importer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-gray-600">
                  Required envelope: graph-sketch content type, graph-sketch-tool tool type, title, and version-2 sketchData.
                </p>
                <button
                  type="button"
                  onClick={copySchemaTemplate}
                  className="inline-flex items-center gap-2 rounded-lg border border-sigma-200 bg-sigma-50 px-3 py-2 text-sm font-medium text-sigma-700 transition-colors hover:bg-sigma-100"
                >
                  <Copy className="h-4 w-4" />
                  {schemaTemplateCopied ? 'Template copied' : 'Copy template'}
                </button>
              </div>

              <textarea
                value={schemaInput}
                onChange={(event) => {
                  setSchemaInput(event.target.value);
                  if (schemaErrors.length) setSchemaErrors([]);
                }}
                className="min-h-[360px] w-full resize-y rounded-xl border border-gray-300 bg-slate-950 p-4 font-mono text-xs leading-5 text-slate-100 outline-none transition focus:border-sigma-400 focus:ring-2 focus:ring-sigma-100"
                placeholder={GRAPH_SKETCH_SCHEMA_EXAMPLE}
                spellCheck={false}
                autoFocus
                aria-label="Graph document JSON"
              />

              {schemaErrors.length > 0 && (
                <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3" role="alert">
                  <p className="text-sm font-semibold text-red-800">The schema could not be imported.</p>
                  <ul className="mt-2 max-h-36 list-disc space-y-1 overflow-y-auto pl-5 text-xs text-red-700">
                    {schemaErrors.map((error, index) => (
                      <li key={`${error.path}-${index}`}>
                        <span className="font-mono">{error.path}</span> {error.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 px-5 py-4">
              <button
                type="button"
                onClick={closeSchemaImporter}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={importSchemaDraft}
                disabled={!schemaInput.trim()}
                className="inline-flex items-center gap-2 rounded-lg bg-sigma-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-sigma-800 disabled:cursor-not-allowed disabled:bg-sigma-300"
              >
                <ClipboardPaste className="h-4 w-4" />
                Import draft
              </button>
            </div>
          </div>
        </div>
      )}
      {isLibraryOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Saved graph sketches</h3>
                <p className="text-sm text-gray-500">
                  {user?.uid ? 'Open or update graph sketches saved to your account.' : 'Open or update graph sketches saved in this browser.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsLibraryOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 text-gray-500 transition-colors hover:bg-gray-50"
                aria-label="Close saved graph sketches"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {isLibraryLoading ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                  Loading saved graph sketches...
                </div>
              ) : savedSketches.length ? (
                <div className="space-y-3">
                  {savedSketches.map((sketch) => (
                    <div key={sketch.id} className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 px-4 py-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-gray-900">{sketch.title}</p>
                        <p className="text-xs text-gray-500">
                          Updated {sketch.updatedAt instanceof Date && !Number.isNaN(sketch.updatedAt.valueOf())
                            ? sketch.updatedAt.toLocaleString()
                            : 'recently'}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => loadSketch(sketch.id)}
                          className="inline-flex items-center rounded-lg border border-sigma-200 bg-sigma-50 px-3 py-2 text-sm font-medium text-sigma-700 transition-colors hover:bg-sigma-100"
                        >
                          Open
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteSketch(sketch.id)}
                          className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                  No saved graph sketches yet.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GraphSketchTool;
